import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/auth';
import { goToLoginPage } from './browser';
import { isTokenExpired } from './jwt';
import { clearPendingAuthorizationOutsideConsent } from './mcp-consent';
import { createClientId } from './random-id';
import { clearExplicitLogout, isExplicitLogout } from './explicit-logout';
import { forgetPendingSignOut } from './sign-out-state';

export const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // required so the login-intent cookie flows back
  timeout: 15_000,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { accessToken } = useAuthStore.getState();
  // Quem já mandou o próprio Authorization (o `/logout` do "Sair", com o access da sessão que
  // sai) não tem o cabeçalho trocado pelo da memória.
  if (accessToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

/** Rota de renovação; o cookie `platform_refresh` só viaja para `/api/v1/platform/auth/*`. */
export const REFRESH_URL = '/api/v1/platform/auth/refresh';

/** Nome do lock do navegador que serializa a renovação entre as abas da mesma origem. */
export const REFRESH_LOCK_NAME = 'platform-admin-refresh';

/** Prazo do POST de renovação: com o lock entre abas, um pedido pendurado travaria as outras. */
const REFRESH_TIMEOUT_MS = 15_000;

/**
 * Roda a renovação dentro do lock do navegador (`navigator.locks`), quando existe. As abas dividem
 * o cookie `platform_refresh`, e cada `/refresh` o gira: a aba que entra depois apresenta o cookie
 * que a anterior acabou de receber, nunca o já girado. Sem o lock, duas abas podiam apresentar o
 * mesmo cookie, e a perdedora levaria o 401 da corrida da rotação (C4 da change
 * platform-backoffice-hardening). Sem a API de locks, segue sem lock.
 */
function withCrossTabRefreshLock<T>(run: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks || typeof locks.request !== 'function') return run();
  return locks.request(REFRESH_LOCK_NAME, { mode: 'exclusive' }, run) as Promise<T>;
}

/**
 * Corpo das respostas de login (MFA) e de renovação. O refresh nunca vem aqui: ele sai só no
 * cookie `platform_refresh` (`HttpOnly`), que o JavaScript não lê (C8, tarefa 9.4 da change
 * platform-backoffice-hardening).
 */
export interface SessionTokens {
  accessToken: string;
}

/**
 * Login concluído (MFA, recovery ou enroll): o access vai para a memória, e a marca do "Sair"
 * explícito sai, porque o operador entrou de novo.
 */
export function completeLogin(tokens: SessionTokens): void {
  clearExplicitLogout();
  forgetPendingSignOut();
  useAuthStore.getState().setAccessToken(tokens.accessToken);
}

/**
 * `POST /platform/auth/refresh` sem corpo: o refresh vai só no cookie `HttpOnly`, que o navegador
 * anexa sozinho (C8). Usa o `axios` cru, sem os interceptores do `api`, para um 401 daqui não
 * disparar outra renovação.
 */
async function requestRefresh(): Promise<SessionTokens> {
  const response = await axios.post<SessionTokens>(REFRESH_URL, undefined, {
    timeout: REFRESH_TIMEOUT_MS,
    withCredentials: true,
  });
  return response.data;
}

/** A renovação terminou depois de um "Sair" explícito: a sessão não volta sozinha. */
export class SessionEndedByLogoutError extends Error {
  constructor() {
    super('A sessão foi encerrada pelo "Sair" desta aba.');
    this.name = 'SessionEndedByLogoutError';
  }
}

async function postRefresh(): Promise<string> {
  const tokens = await requestRefresh();
  // Uma renovação que já estava em voo quando o operador clicou "Sair" não restaura a sessão.
  if (isExplicitLogout()) throw new SessionEndedByLogoutError();
  useAuthStore.getState().setAccessToken(tokens.accessToken);
  return tokens.accessToken;
}

/**
 * Se o `/refresh` recusou a sessão, e não apenas falhou. O 401 é a recusa da API com o cookie
 * (C8). O 400 e o 415 seriam uma API anterior ao cookie, que exige o refresh no corpo: o SPA não
 * tem refresh nenhum para mandar (ele só existe no cookie `HttpOnly`), então não há sessão a
 * restaurar; o caminho é o login, e não a tela de "tente de novo" (que repetiria o mesmo 415 para
 * sempre).
 */
export function isRefreshRejected(err: unknown): boolean {
  if (!(err instanceof AxiosError)) return false;
  const status = err.response?.status;
  return status === 401 || status === 400 || status === 415;
}

let refreshPromise: Promise<string> | null = null;

/**
 * Troca o cookie por um access novo, dentro do lock entre abas, com uma só renovação em voo por
 * aba: chamadas concorrentes (vários 401 ao mesmo tempo, ou o carregamento e um 401) esperam a
 * mesma promessa. Rejeita com o erro do `/refresh`, sem limpar nada: quem chama decide.
 */
export function renewSessionFromCookie(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = withCrossTabRefreshLock(postRefresh).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * Access novo só para repetir o `/logout`, sem gravá-lo na memória: o "Sair" já limpou a sessão, e
 * ela não pode voltar por aqui.
 */
async function accessTokenForLogout(): Promise<string> {
  const tokens = await withCrossTabRefreshLock(requestRefresh);
  return tokens.accessToken;
}

/**
 * O `/refresh` recusou a sessão ({@link isRefreshRejected}): ela acabou. Limpa a memória e manda
 * ao login.
 * Na página de consentimento, a query pendente fica no sessionStorage (`src/lib/mcp-consent.ts`)
 * e o login volta a ela. Em qualquer outra página, a sessão expirada não leva a um pedido do
 * agente, e a chave sai junto com a sessão.
 */
function endSessionAndGoToLogin(): void {
  useAuthStore.getState().clearAuth();
  clearPendingAuthorizationOutsideConsent(window.location.pathname);
  goToLoginPage();
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const status = error.response?.status ?? 0;
    const original = error.config;

    if (
      status === 401 &&
      original &&
      !(original as unknown as Record<string, unknown>)._retried
    ) {
      const { accessToken } = useAuthStore.getState();
      if (accessToken && isTokenExpired(accessToken, 0)) {
        (original as unknown as Record<string, unknown>)._retried = true;

        let newToken: string;
        try {
          newToken = await renewSessionFromCookie();
        } catch (refreshError) {
          // Só a recusa do `/refresh` (401, ou 400/415 da API anterior ao cookie) encerra a sessão.
          // 429, 5xx e falha de rede deixam a sessão como está: a chamada original falha, e a
          // próxima tenta renovar de novo.
          if (isRefreshRejected(refreshError)) {
            endSessionAndGoToLogin();
          }
          return Promise.reject(error);
        }
        original.headers!.Authorization = `Bearer ${newToken}`;
        return api(original);
      }
    }

    return Promise.reject(error);
  },
);

export const LOGOUT_URL = '/platform/auth/logout';

/** Mensagem das telas quando o "Sair" não conseguiu encerrar a sessão na API. */
export const LOGOUT_FAILED_MESSAGE =
  'Não foi possível encerrar a sessão no servidor. Confira a conexão e tente sair de novo.';

/**
 * Encerra a sessão na API (`POST /platform/auth/logout`), que revoga a sessão e apaga o cookie
 * `platform_refresh` (C8). Resolve só quando não sobrou sessão que o cookie restaure; rejeita nos
 * demais casos.
 *
 * `accessToken` é o access da sessão que sai; sem ele, o da memória. O "Sair" do `Shell` limpa a
 * memória antes (`lib/sign-out.ts`) e passa o access que guardou.
 *
 * - 2xx: a API revogou a sessão e mandou o `Set-Cookie` vencido.
 * - 401: o access foi recusado (sessão revogada, ou relógio do navegador atrasado, que o
 *   interceptor não vê como vencido porque olha o relógio local). Renova pelo cookie, sem olhar o
 *   relógio e sem gravar o access novo na memória, e repete o logout uma vez com ele. Se o
 *   `/refresh` recusar o cookie ({@link isRefreshRejected}), a sessão já acabou e a API apagou o
 *   cookie: resolve.
 * - Qualquer outra falha (5xx, timeout, rede, 429 do `/refresh`): rejeita.
 */
export async function logoutPlatformSession(accessToken?: string | null): Promise<void> {
  const token = accessToken ?? useAuthStore.getState().accessToken;
  try {
    await api.post(LOGOUT_URL, {}, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
    return;
  } catch (err) {
    if (problemStatus(err) !== 401) throw err;
  }

  let renewed: string;
  try {
    renewed = await accessTokenForLogout();
  } catch (refreshError) {
    if (isRefreshRejected(refreshError)) return;
    throw refreshError;
  }

  await api.post(LOGOUT_URL, {}, { headers: { Authorization: `Bearer ${renewed}` } });
}
/**
 * Reads `detail` / `title` from RFC 9457 ProblemDetails payloads when the
 * server returns one; falls back to a generic message otherwise.
 */
/**
 * Mensagens dos 409 do `IdempotencyKeyFilter` (tarefa 4.2 da change platform-backoffice-hardening).
 * Os formulários comerciais reaproveitam a chave na repetição do mesmo pedido depois de um timeout,
 * de uma falha de rede ou de um 5xx (`useIdempotencyKey`, em `lib/idempotency.ts`); o 409 diz o que
 * aconteceu com o envio anterior que usou a mesma chave, e nenhum deles repete o efeito.
 */
export const IDEMPOTENCY_MESSAGES: Readonly<Record<string, string>> = {
  'idempotency.in_progress':
    'Pedido ainda em processamento; aguarde e confira o histórico antes de repetir.',
  'idempotency.previous_attempt_failed':
    'A tentativa anterior falhou no meio; confira o histórico do tenant antes de tentar de novo com um novo envio.',
  'idempotency.key_reused':
    'Este envio já foi usado com outros dados; confira o histórico do tenant e envie de novo.',
  'idempotency.replay_unavailable':
    'O pedido já foi concluído, e a resposta dele não fica guardada; confira o histórico do tenant.',
};

/**
 * Cabeçalho do step-up por TOTP (change platform-backoffice-hardening, C6): desativar tenant,
 * cancelar assinatura e aplicar preço às assinaturas de verdade levam o código atual do
 * autenticador do operador. A API confere o código antes da `Idempotency-Key`, e o 403 de step-up
 * não consome a chave (ver `keepsIdempotencyKey`). Aprovar a concessão de um agente do MCP
 * (`/mcp/autorizar`, tarefa 7.3) também leva o código, mas sem `Idempotency-Key`.
 */
export const STEP_UP_HEADER = 'X-Platform-Step-Up';

/**
 * Cabeçalho do step-up por passkey (C14, tarefa 14.5): a asserção WebAuthn do desafio de
 * `POST /platform/auth/step-up/passkey/options`, em base64url. Vale onde o TOTP vale.
 */
export const STEP_UP_PASSKEY_HEADER = 'X-Platform-Step-Up-Passkey';

/**
 * A confirmação de uma ação sensível: o código TOTP (texto de 6 dígitos) ou a asserção de passkey
 * já codificada para o cabeçalho (`{ passkey }`).
 */
export type StepUpProof = string | { passkey: string };

/** As opções de escrita que levam a confirmação: `stepUpCode` (TOTP) ou `stepUpPasskey`. */
export function stepUpOptions(proof: StepUpProof): Pick<SensitiveWriteOptions, 'stepUpCode' | 'stepUpPasskey'> {
  return typeof proof === 'string' ? { stepUpCode: proof } : { stepUpPasskey: proof.passkey };
}

/** Os cabeçalhos da confirmação, para as rotas sem `Idempotency-Key` (o approve do consentimento). */
export function stepUpHeaders(proof: StepUpProof): Record<string, string> {
  return typeof proof === 'string' ? { [STEP_UP_HEADER]: proof } : { [STEP_UP_PASSKEY_HEADER]: proof.passkey };
}

/**
 * Mensagens das recusas do step-up e do freio de segundo fator (C5): a falha do step-up conta para
 * o mesmo bloqueio do login (423 `platform.auth.locked`), e a tentativa só é conferida com vaga
 * livre (429 `platform.auth.attempts_in_flight`).
 */
export const STEP_UP_MESSAGES: Readonly<Record<string, string>> = {
  'platform.step_up_required': 'Confirme com o código do autenticador.',
  'platform.step_up_invalid':
    'Código inválido ou já usado. Aguarde o próximo código do autenticador e tente de novo.',
  'platform.step_up_rate_limited':
    'Muitas tentativas de confirmação em pouco tempo. Aguarde um minuto e digite o código atual do autenticador.',
  'platform.auth.attempts_in_flight':
    'Outra confirmação desta conta está em andamento. Aguarde alguns segundos e tente de novo.',
  'platform.auth.locked':
    'Conta bloqueada temporariamente por tentativas erradas. Aguarde alguns minutos ou peça o desbloqueio a outro Admin.',
  // Passkey do operador (C14).
  'platform.passkey.not_configured': 'Passkey não está disponível neste ambiente.',
  'platform.passkey.session_not_eligible':
    'Cadastrar passkey exige uma sessão aberta com senha e código do autenticador. Saia e entre de novo, sem recovery code.',
  'platform.passkey.limit_reached': 'A conta já tem 5 passkeys ativas. Revogue uma antes de cadastrar outra.',
  'platform.passkey.not_registered': 'Esta conta não tem passkey ativa. Confirme com o código do autenticador.',
  'platform.passkey.already_registered': 'Esta passkey já está cadastrada.',
  'platform.passkey.not_owner': 'Só o dono da passkey ou um administrador pode revogá-la.',
  'platform.passkey.too_many_challenges': 'Muitos pedidos de confirmação em aberto. Aguarde alguns minutos.',
};

export function describeError(err: unknown): string {
  // Só as chaves próprias dos mapas: um `errorCode` como `toString` ou `constructor` acharia o
  // método herdado de `Object.prototype` e a tela mostraria uma função no lugar do motivo.
  const code = problemErrorCode(err);
  if (code && Object.hasOwn(IDEMPOTENCY_MESSAGES, code)) return IDEMPOTENCY_MESSAGES[code];
  if (code && Object.hasOwn(STEP_UP_MESSAGES, code)) return STEP_UP_MESSAGES[code];

  if (err instanceof AxiosError) {
    const data = err.response?.data as
      | { detail?: string; title?: string; errors?: Record<string, string[]> }
      | undefined;
    if (data?.detail) return data.detail;
    if (data?.title) return data.title;
    if (data?.errors) {
      const first = Object.values(data.errors)[0];
      if (Array.isArray(first) && first.length > 0) return first[0];
    }
    return err.message;
  }
  return err instanceof Error ? err.message : 'Erro inesperado.';
}

export function newIdempotencyKey(): string {
  return createClientId();
}

/**
 * Cabeçalhos de uma escrita com `Idempotency-Key` e, nas ações sensíveis, o step-up. A tela que
 * repete o mesmo pedido passa a chave dele (`useIdempotencyKey`); sem chave, sai uma nova.
 */
export interface SensitiveWriteOptions {
  idempotencyKey?: string;
  /** Código TOTP atual do operador, mandado em `X-Platform-Step-Up`. */
  stepUpCode?: string;
  /** Asserção de passkey já codificada, mandada em `X-Platform-Step-Up-Passkey` (C14). */
  stepUpPasskey?: string;
}

function sensitiveWriteHeaders(options: SensitiveWriteOptions): Record<string, string> {
  const headers: Record<string, string> = {
    'Idempotency-Key': options.idempotencyKey ?? newIdempotencyKey(),
  };
  if (options.stepUpCode) headers[STEP_UP_HEADER] = options.stepUpCode;
  if (options.stepUpPasskey) headers[STEP_UP_PASSKEY_HEADER] = options.stepUpPasskey;
  return headers;
}

// =============================================================================
//  Platform-accounts (operators) — change platform-backoffice-account-management
// =============================================================================

export type OperatorRole = 'SupportL1' | 'SupportL2' | 'Commercial' | 'Admin';
export type OperatorStatus = 'Pending' | 'Active' | 'Revoked';

export interface OperatorDto {
  id: string;
  email: string;
  fullName: string;
  role: OperatorRole;
  status: OperatorStatus;
  mfaEnabledAt: string | null;
  mustEnrollMfa: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  grantedBy: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
  pendingInvitationExpiresAt: string | null;
  /** Total invitations issued (original + resends). UI displays `invitationCount - 1` as "Reenvios". */
  invitationCount: number;
  /** Timestamp (ISO) of the most recent invitation issued. `null` only when no invitation exists. */
  lastInvitationIssuedAt: string | null;
  /**
   * Fim (ISO, UTC) do bloqueio por falhas de segundo fator, ou nulo (change
   * platform-backoffice-hardening, C5). Opcional: uma API anterior não manda o campo.
   */
  lockedUntil?: string | null;
}

export interface OperatorListResponse {
  items: OperatorDto[];
  page: number;
  pageSize: number;
  total: number;
}

export interface CreateOperatorResponse {
  account: OperatorDto;
  verificationCode: string;
}

export interface CodeOnlyResponse {
  verificationCode: string;
}

export interface InvitationLookupResponse {
  email: string;
  fullName: string;
  expiresAt: string;
}

export interface SensitiveEventActor {
  id: string;
  fullName: string;
  email: string;
}

export interface SensitiveEventDto {
  id: string;
  action: string;
  actor: SensitiveEventActor;
  target: SensitiveEventActor | null;
  reason: string | null;
  timestamp: string;
  ip: string;
  sensitivity: string;
}

export interface SensitiveEventsResponse {
  items: SensitiveEventDto[];
  rangeDays: number;
}

export async function listOperators(params: {
  page?: number;
  pageSize?: number;
  role?: OperatorRole;
  status?: OperatorStatus;
}): Promise<OperatorListResponse> {
  const { data } = await api.get<OperatorListResponse>('/platform/accounts/', { params });
  return data;
}

export async function getOperator(id: string): Promise<OperatorDto> {
  const { data } = await api.get<OperatorDto>(`/platform/accounts/${id}`);
  return data;
}

export async function createOperator(payload: {
  email: string;
  fullName: string;
  role: OperatorRole;
}): Promise<CreateOperatorResponse> {
  const { data } = await api.post<CreateOperatorResponse>('/platform/accounts/', payload);
  return data;
}

export async function updateOperator(
  id: string,
  payload: { fullName?: string; role?: OperatorRole },
): Promise<OperatorDto> {
  const { data } = await api.patch<OperatorDto>(`/platform/accounts/${id}`, payload);
  return data;
}

export async function resetOperatorPassword(id: string, reason: string): Promise<CodeOnlyResponse> {
  const { data } = await api.post<CodeOnlyResponse>(
    `/platform/accounts/${id}/reset-password`,
    { reason },
  );
  return data;
}

export async function resetOperatorMfa(id: string, reason: string): Promise<OperatorDto> {
  const { data } = await api.post<OperatorDto>(
    `/platform/accounts/${id}/reset-mfa`,
    { reason },
  );
  return data;
}

export async function resendOperatorInvitation(id: string): Promise<CodeOnlyResponse> {
  const { data } = await api.post<CodeOnlyResponse>(
    `/platform/accounts/${id}/resend-invitation`,
  );
  return data;
}

export async function revokeOperator(id: string, reason: string): Promise<void> {
  await api.post(`/platform/accounts/${id}/revoke`, { reason });
}

/**
 * Desbloqueia a conta bloqueada por falhas de segundo fator (change
 * platform-backoffice-hardening, C5). Só Admin e nunca a própria conta (403); devolve a conta
 * com `lockedUntil` nulo.
 */
export async function unlockAccount(id: string): Promise<OperatorDto> {
  const { data } = await api.post<OperatorDto>(`/platform/accounts/${id}/unlock`);
  return data;
}

export async function lookupInvitation(token: string): Promise<InvitationLookupResponse> {
  const { data } = await api.post<InvitationLookupResponse>(
    '/platform/accounts/auth/invitations/lookup',
    { token },
  );
  return data;
}

export async function activateAccount(payload: {
  token: string;
  verificationCode: string;
  newPassword: string;
}): Promise<void> {
  await api.post('/platform/accounts/auth/activate', payload);
}

export async function listSensitiveEvents(days = 7): Promise<SensitiveEventsResponse> {
  const { data } = await api.get<SensitiveEventsResponse>(
    '/platform/auth/sensitive-events',
    { params: { days } },
  );
  return data;
}

/**
 * Reads the RFC 9457 problem-details <c>code</c> extension that the platform
 * backend uses to disambiguate 409 / 503 cases (see exception handlers in
 * VaptMenu.Modules.Platform.Identity.Infrastructure.Errors).
 */
export function problemCode(err: unknown): string | undefined {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { code?: string } | undefined;
    return data?.code;
  }
  return undefined;
}

/**
 * Pulls the remainingAttempts extension off a 422 from /accounts/auth/activate.
 */
export function remainingAttempts(err: unknown): number | undefined {
  if (err instanceof AxiosError) {
    const data = err.response?.data as { remainingAttempts?: number } | undefined;
    return data?.remainingAttempts;
  }
  return undefined;
}

// =============================================================================
//  Platform Feature Flags
// =============================================================================

export type FeatureFlagScope = 'Global' | 'PerTenant';

export interface FeatureFlagDto {
  id: string;
  key: string;
  displayName: string;
  description: string | null;
  defaultEnabled: boolean;
  defaultRolloutPercentage: number | null;
  scope: FeatureFlagScope;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
  tenantOverrideCount: number;
}

export interface FeatureFlagTenantOverrideDto {
  tenantId: string;
  enabled: boolean;
  rolloutPercentage: number | null;
  updatedAt: string;
  updatedBy: string | null;
}

export interface FeatureFlagDetailDto {
  flag: FeatureFlagDto;
  tenantOverrides: FeatureFlagTenantOverrideDto[];
}

export interface FeatureFlagListResponse {
  items: FeatureFlagDto[];
  page: number;
  pageSize: number;
  total: number;
}

export async function listFeatureFlags(params: {
  page?: number;
  pageSize?: number;
  scope?: FeatureFlagScope;
  status?: 'Active' | 'Archived';
}): Promise<FeatureFlagListResponse> {
  const { data } = await api.get<FeatureFlagListResponse>('/platform/feature-flags/', { params });
  return data;
}

export async function getFeatureFlag(key: string): Promise<FeatureFlagDetailDto> {
  const { data } = await api.get<FeatureFlagDetailDto>(`/platform/feature-flags/${encodeURIComponent(key)}`);
  return data;
}

export async function createFeatureFlag(payload: {
  key: string;
  displayName: string;
  description?: string;
  defaultEnabled: boolean;
  defaultRolloutPercentage?: number | null;
  scope: FeatureFlagScope;
}): Promise<FeatureFlagDto> {
  const { data } = await api.post<FeatureFlagDto>('/platform/feature-flags/', payload);
  return data;
}

export async function updateFeatureFlag(
  key: string,
  payload: {
    displayName?: string;
    description?: string;
    defaultEnabled?: boolean;
    defaultRolloutPercentage?: number | null;
  },
): Promise<FeatureFlagDto> {
  const { data } = await api.patch<FeatureFlagDto>(
    `/platform/feature-flags/${encodeURIComponent(key)}`,
    payload,
  );
  return data;
}

export async function archiveFeatureFlag(key: string): Promise<void> {
  await api.post(`/platform/feature-flags/${encodeURIComponent(key)}/archive`);
}

export async function setFeatureFlagTenantOverride(
  key: string,
  tenantId: string,
  payload: { enabled: boolean; rolloutPercentage?: number | null },
): Promise<FeatureFlagTenantOverrideDto> {
  const { data } = await api.put<FeatureFlagTenantOverrideDto>(
    `/platform/feature-flags/${encodeURIComponent(key)}/tenants/${tenantId}`,
    payload,
  );
  return data;
}

export async function clearFeatureFlagTenantOverride(
  key: string,
  tenantId: string,
): Promise<void> {
  await api.delete(`/platform/feature-flags/${encodeURIComponent(key)}/tenants/${tenantId}`);
}

// --- Feature flag catalog (declared flags × configured state) --------------

export interface FeatureFlagCatalogItem {
  key: string;
  displayName: string;
  description: string;
  scope: FeatureFlagScope;
  defaultEnabled: boolean;
  defaultRolloutPercentage: number | null;
  audience: string;
  configured: boolean;
  archived: boolean;
  currentDefaultEnabled: boolean | null;
  tenantOverrideCount: number | null;
  /**
   * Desligamento de emergência (Global): só liga e desliga. A API recusa percentual
   * diferente de vazio e de 100 e recusa arquivar (change platform-backoffice-hardening, H-10).
   */
  isKillSwitch: boolean;
}

/** Returns the declarative flag catalog crossed with this environment's state. */
export async function getFeatureFlagCatalog(): Promise<FeatureFlagCatalogItem[]> {
  const { data } = await api.get<{ items: FeatureFlagCatalogItem[] }>(
    '/platform/feature-flags/catalog',
  );
  return data.items;
}

// --- Tenant lookup (powers the TenantSelector) -----------------------------

export interface TenantSummary {
  id: string;
  tradeName: string;
  ownerName: string;
  slug: string;
  phone: string;
  isActive: boolean;
  subscriptionStatus: string;
  /**
   * URL canônica do cardápio público, calculada no servidor a partir do slug e
   * do root domain do ambiente. Nunca montar no cliente: staging e produção têm
   * domínios diferentes e só o backend sabe qual está valendo.
   */
  menuUrl?: string;
}

/** Searches tenants by name/owner/slug/phone. Returns the first page of matches. */
export async function searchTenants(q: string, pageSize = 10): Promise<TenantSummary[]> {
  const { data } = await api.get<{ items: TenantSummary[] }>('/platform/tenants', {
    params: { q: q || undefined, page: 1, pageSize },
  });
  return data.items;
}

/** Resolves a single tenant by id (best-effort; returns null if not found). */
export async function getTenantSummary(id: string): Promise<TenantSummary | null> {
  try {
    const { data } = await api.get<TenantSummary>(`/platform/tenants/${id}`);
    return data;
  } catch {
    return null;
  }
}

// =============================================================================
//  Ciclo de vida e assinatura do tenant (só Admin, com step-up, change
//  platform-backoffice-hardening, C6)
// =============================================================================

/** Categoria do motivo da desativação (`TenantDeactivationReason` no servidor). */
export interface DeactivateTenantPayload {
  reasonCategory: string;
  reasonDetail: string;
}

export interface DeactivateTenantResponse {
  tenantId: string;
  isActive: boolean;
  /** Assinatura que segue cobrando no gateway; nulo quando não há. */
  activeGatewaySubscription?: { subscriptionId: string; warning?: string } | null;
}

export function deactivateTenantUrl(tenantId: string): string {
  return `/platform/tenants/${tenantId}/deactivate`;
}

/** Tira a loja do ar. Exige o step-up (`stepUpCode`) e a `Idempotency-Key` da tentativa. */
export async function deactivateTenant(
  tenantId: string,
  payload: DeactivateTenantPayload,
  options: SensitiveWriteOptions,
): Promise<DeactivateTenantResponse> {
  const { data } = await api.post<DeactivateTenantResponse>(deactivateTenantUrl(tenantId), payload, {
    headers: sensitiveWriteHeaders(options),
  });
  return data;
}

export interface CancelTenantSubscriptionResponse {
  tenantId: string;
  subscriptionId: string | null;
  status: string;
  /** `completed` (200) ou `reconciliation_pending` (202). */
  cancellationStatus: string;
  /** Até quando o acesso segue, pelo período já pago. */
  paidThroughEndsAt: string | null;
  note?: string;
}

export function cancelTenantSubscriptionUrl(tenantId: string): string {
  return `/platform/tenants/${tenantId}/subscription/cancel`;
}

/**
 * Encerra a recorrência no gateway (irreversível: o gateway não restaura a assinatura). Exige o
 * step-up e a `Idempotency-Key`; o 202 é aceito (reconciliação financeira em processamento), e
 * depois de um 5xx do gateway a mesma chave retoma o cancelamento.
 */
export async function cancelTenantSubscription(
  tenantId: string,
  reason: string,
  options: SensitiveWriteOptions,
): Promise<CancelTenantSubscriptionResponse> {
  const { data } = await api.post<CancelTenantSubscriptionResponse>(
    cancelTenantSubscriptionUrl(tenantId),
    { reason },
    { headers: sensitiveWriteHeaders(options) },
  );
  return data;
}

// =============================================================================
//  Busca de pedidos no backoffice
// =============================================================================

export interface PlatformOrderListItem {
  id: string;
  trackingCode: string;
  status: string;
  deliveryMethod: string;
  customerName: string;
  customerPhone: string;
  total: number;
  createdAt: string;
  completedAt: string | null;
  operationallyPurgedAt: string | null;
}

export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface OrderSearchFilters {
  q?: string;
  status?: string;
  deliveryMethod?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

/** Lista os pedidos de um tenant. O escopo vem da rota — nunca de um filtro. */
export async function searchTenantOrders(
  tenantId: string,
  filters: OrderSearchFilters = {},
): Promise<PagedResult<PlatformOrderListItem>> {
  const { data } = await api.get<PagedResult<PlatformOrderListItem>>(
    `/platform/tenants/${tenantId}/orders`,
    {
      params: {
        q: filters.q || undefined,
        status: filters.status || undefined,
        deliveryMethod: filters.deliveryMethod || undefined,
        from: filters.from || undefined,
        to: filters.to || undefined,
        page: filters.page ?? 1,
        pageSize: filters.pageSize ?? 20,
      },
    },
  );
  return data;
}

export interface OrderLookupMatch {
  tenantId: string;
  tenantTradeName: string;
  tenantSlug: string;
  orderId: string;
  trackingCode: string;
  status: string;
  createdAt: string;
}

/**
 * Descobre a que tenant pertence um código de rastreio. O código é único em
 * toda a base (índice único `IX_orders_TrackingCode`), então há no máximo uma
 * correspondência. `null` quando o código não existe (404 na API).
 */
export async function lookupOrderByTrackingCode(
  trackingCode: string,
): Promise<OrderLookupMatch | null> {
  try {
    const { data } = await api.get<OrderLookupMatch>('/platform/orders/lookup', {
      params: { trackingCode },
    });
    return data;
  } catch (err) {
    if (err instanceof AxiosError && err.response?.status === 404) return null;
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Preco do plano (change platform-pricing-config; por plano desde billing-plans)
// ---------------------------------------------------------------------------

export interface PricingVersionDto {
  id: string;
  monthlyPrice: number;
  yearlyPrice: number;
  /** Atendimentos inclusos por mês. Ausente numa API de antes dos planos. */
  includedAiUnitsPerMonth?: number;
  effectiveFrom: string;
  createdAt: string;
  createdBy: string | null;
  note: string | null;
}

export interface PricingResponse {
  /** O plano das versões. Ausente numa API de antes dos planos (era sempre o Essencial). */
  planCode?: string;
  current: PricingVersionDto | null;
  scheduled: PricingVersionDto | null;
  history: PricingVersionDto[];
}

export interface CreatePricingVersionPayload {
  /** Plano da versão; omitido = Essencial. */
  planCode?: string;
  monthlyPrice: number;
  yearlyPrice: number;
  /** Atendimentos inclusos por mês (0 a 100.000); omitido = 0. */
  includedAiUnitsPerMonth?: number;
  /** ISO 8601; omitido = vigencia imediata. */
  effectiveFrom?: string | null;
  note?: string | null;
}

/** Versão vigente de um plano no catálogo do backoffice. */
export interface PlatformPlanVersion {
  monthlyPrice: number;
  yearlyPrice: number;
  includedAiUnitsPerMonth: number;
  effectiveFrom: string;
}

/**
 * Um plano do catálogo (`GET /platform/plans`). As contagens somam todos os
 * tenants; a resposta não traz nenhum identificador.
 */
export interface PlatformPlan {
  code: string;
  name: string;
  description: string | null;
  isPublic: boolean;
  sortOrder: number;
  archivedAt: string | null;
  features: string[];
  current: PlatformPlanVersion | null;
  currentSubscriptions: number;
  activeOverrides: number;
}

/** Código do plano que a API usa quando nenhum é informado. */
export const ESSENTIAL_PLAN_CODE = 'essential';
/** O teste grátis: nunca tem preço nem é vendido. */
export const TRIAL_PLAN_CODE = 'trial';

/**
 * Catálogo de planos para a página "Planos e preços". `null` quando a API
 * ainda não expõe o catálogo (404 de uma imagem anterior): a página cai na
 * visão de um plano só, como antes.
 */
export async function getPlatformPlans(): Promise<PlatformPlan[] | null> {
  try {
    const { data } = await api.get<{ plans: PlatformPlan[] }>('/platform/plans');
    return data.plans;
  } catch (err) {
    if (err instanceof AxiosError && err.response?.status === 404) return null;
    throw err;
  }
}

export type PricingApplyOutcome = 'planned' | 'updated' | 'skipped' | 'failed';

export interface PricingApplyItem {
  subscriptionId: string;
  tenantId: string;
  billingCycle: 'monthly' | 'yearly';
  currentValue: number;
  newValue: number;
  hasActiveDiscount: boolean;
  outcome: PricingApplyOutcome;
  reason: string | null;
}

export interface PricingApplyResponse {
  dryRun: boolean;
  /** O plano aplicado. Ausente numa API de antes dos planos. */
  planCode?: string;
  monthlyPrice: number;
  yearlyPrice: number;
  updated: number;
  skipped: number;
  failed: number;
  items: PricingApplyItem[];
}

/** Versões de preço de um plano; sem plano, as do Essencial. */
export async function getPricing(planCode?: string): Promise<PricingResponse> {
  const { data } = await api.get<PricingResponse>('/platform/pricing/', {
    params: planCode ? { planCode } : undefined,
  });
  return data;
}

export async function createPricingVersion(payload: CreatePricingVersionPayload): Promise<PricingVersionDto> {
  const { data } = await api.post<PricingVersionDto>('/platform/pricing/', payload, {
    headers: { 'Idempotency-Key': newIdempotencyKey() },
  });
  return data;
}

export async function deletePricingVersion(id: string): Promise<void> {
  await api.delete(`/platform/pricing/${encodeURIComponent(id)}`);
}

/**
 * Reprecifica as assinaturas ativas de um plano com o preco vigente dele.
 * `dryRun: true` so lista o impacto; a pagina exige o dry-run antes de
 * permitir a aplicacao. O plano e obrigatorio na API: sem ele, 400 e nada e
 * avaliado. A aplicacao de verdade (`dryRun: false`) exige o step-up
 * (`stepUpCode`); o dry-run nao manda o cabecalho.
 */
export const APPLY_PRICING_URL = '/platform/pricing/apply-to-subscriptions';

export async function applyPricingToSubscriptions(
  planCode: string,
  dryRun: boolean,
  options: SensitiveWriteOptions = {},
): Promise<PricingApplyResponse> {
  const { data } = await api.post<PricingApplyResponse>(
    APPLY_PRICING_URL,
    { planCode, dryRun },
    { headers: sensitiveWriteHeaders(dryRun ? { idempotencyKey: options.idempotencyKey } : options) },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Plano do tenant e cortesia de plano (change billing-plans)
// ---------------------------------------------------------------------------

/** De onde vem o plano efetivo do tenant. */
export type EntitlementSource = 'none' | 'trial' | 'subscription' | 'override';
export type PlanOverrideStatus = 'active' | 'ended' | 'cancelled';
export type PlanChangeKind = 'upgrade' | 'downgrade';
export type PlanChangeStatus =
  | 'awaiting_payment'
  | 'applying'
  | 'scheduled'
  | 'completed'
  | 'failed_retryable'
  | 'cancelled';

export interface PlanOverrideView {
  id: string;
  tenantId: string;
  planCode: string;
  planName: string;
  startsAt: string;
  /** Fim exclusivo da cortesia. */
  endsAt: string;
  reason: string;
  status: PlanOverrideStatus;
  createdByOperatorId: string;
  cancelledAt: string | null;
  cancelledByOperatorId: string | null;
  cancellationReason: string | null;
}

export interface TenantPlanSubscription {
  id: string;
  planCode: string | null;
  planName: string | null;
  billingCycle: 'monthly' | 'yearly';
  status: string;
  /** O que o Asaas cobra a cada ciclo, com o desconto ativo. */
  value: number;
  /** O valor cheio do ciclo. */
  fullValue: number;
  nextDueDate: string | null;
  accessEndsAt: string | null;
}

export interface TenantPlanChange {
  id: string;
  kind: PlanChangeKind;
  status: PlanChangeStatus;
  toPlanCode: string | null;
  toPlanName: string | null;
  prorationAmount: number;
  effectiveAt: string | null;
  paymentDueDate: string | null;
  createdAt: string;
}

/** `GET /platform/tenants/{tenantId}/plan`. */
export interface TenantPlanOverview {
  tenantId: string;
  planCode: string | null;
  planName: string | null;
  source: EntitlementSource;
  features: string[];
  overrideEndsAt: string | null;
  subscription: TenantPlanSubscription | null;
  pendingPlanChange: TenantPlanChange | null;
  activeOverride: PlanOverrideView | null;
  /** Falso quando a cortesia vigente não decide o plano (plano igual ou menor, ou tenant sem acesso). */
  activeOverrideInEffect: boolean;
  overrides: PlanOverrideView[];
}

export interface PlanOverrideResponse extends PlanOverrideView {
  effectivePlanCode: string | null;
  effectiveSource: EntitlementSource | null;
}

export async function getTenantPlan(tenantId: string): Promise<TenantPlanOverview> {
  const { data } = await api.get<TenantPlanOverview>(`/platform/tenants/${encodeURIComponent(tenantId)}/plan`);
  return data;
}

/** Dá a cortesia de plano de agora até `endsAt` (exclusivo). */
export async function grantPlanOverride(
  tenantId: string,
  payload: { planCode: string; endsAt: string; reason: string },
): Promise<PlanOverrideResponse> {
  const { data } = await api.post<PlanOverrideResponse>(
    `/platform/tenants/${encodeURIComponent(tenantId)}/plan-override`,
    payload,
    { headers: { 'Idempotency-Key': newIdempotencyKey() } },
  );
  return data;
}

/** Encerra agora a cortesia vigente. */
export async function cancelPlanOverride(tenantId: string, reason: string): Promise<PlanOverrideResponse> {
  const { data } = await api.post<PlanOverrideResponse>(
    `/platform/tenants/${encodeURIComponent(tenantId)}/plan-override/cancel`,
    { reason },
    { headers: { 'Idempotency-Key': newIdempotencyKey() } },
  );
  return data;
}

/* ---- Vapt IA ---- */

export interface VaptAiUsageTenantDto {
  tenantId: string;
  tenantName: string;
  turns: number;
  inputTokens: number;
  outputTokens: number;
  estimatedCostUsd: number;
}

export interface VaptAiUsagePlatformDto {
  inputTokens: number;
  outputTokens: number;
  estimatedCostUsd: number;
  budgetTokens: number;
  usedPercent: number;
}

export interface VaptAiUsageResponse {
  month: string;
  platform: VaptAiUsagePlatformDto;
  tenants: VaptAiUsageTenantDto[];
}

/**
 * Consumo de IA da plataforma no mes, por tenant e no total.
 *
 * Rota cross-tenant por design: nao ha tenantId no path, porque a pergunta e
 * sobre a plataforma inteira. A resposta traz apenas contagens — nunca texto de
 * conversa, argumento de ferramenta ou nome de item.
 */
export async function getVaptAiUsage(month?: string): Promise<VaptAiUsageResponse> {
  const { data } = await api.get<VaptAiUsageResponse>('/platform/vapt-ai/usage', {
    params: month ? { month } : undefined,
  });
  return data;
}

/* ---- Impressão ---- */

/** Frota da impressão: só contagens e versões, sem id de loja, pedido ou computador. */
export interface PrintingFleetResponse {
  stores: { active: number; paused: number; deactivatedLast30Days: number };
  agents: { online: number; offline: number };
  agentVersions: Array<{ version: string | null; agents: number }>;
  protocolVersions: Array<{ version: number | null; agents: number }>;
  jobsLast24h: {
    total: number;
    pending: number;
    delivered: number;
    printed: number;
    failed: number;
    cancelled: number;
    expired: number;
  };
  incidentsLast24h: {
    overdue: number;
    failed: number;
    renderFailed: number;
    dispatchIsolated: number;
  };
}

/**
 * Frota da impressão nos pontos de produção.
 *
 * Rota cross-tenant por design, como a do Vapt IA: a resposta traz apenas
 * contagens e versões — nenhum id de loja, pedido ou computador, e nada de
 * cliente. Quem quer uma loja abre o detalhe do tenant.
 */
export async function getPrintingFleet(): Promise<PrintingFleetResponse> {
  const { data } = await api.get<PrintingFleetResponse>('/platform/printing/fleet');
  return data;
}

/** As duas flags juntas: fora do piloto, desligada pela Vapt ou disponível. */
export type PrintingAvailability = 'outside_pilot' | 'platform_disabled' | 'available';
export type PrintingActivation = 'never_activated' | 'active' | 'deactivated';
export type PrintAgentStatus = 'active' | 'revoked';
export type PrintAgentRevokeReason = 'manual' | 'credential_reuse' | 'store_deactivated';
export type PrinterOrigin = 'reported' | 'network';
export type PrinterTransport = 'spooler' | 'network' | 'driver';
/**
 * Largura do papel. `mm80_512` é o papel de 80 mm em cabeça de 180 dpi (ex.:
 * Epson TM-T88), que imprime 512 pontos em vez dos 576 do `mm80`.
 */
export type PrinterWidth = 'mm58' | 'mm80' | 'mm80_512';
export type PrinterState = 'unknown' | 'ok' | 'unreachable' | 'missing';
export type PrintJobErrorCode =
  | 'printer_unreachable'
  | 'printer_error'
  | 'printer_unavailable'
  | 'render_failed';
export type PrintTicketKind = 'production' | 'checkout' | 'delivery' | 'cancellation' | 'test';
export type PrintAgentEventType =
  | 'paired'
  | 'connected'
  | 'disconnected'
  | 'token_refused'
  | 'credential_reuse_detected'
  | 'revoked'
  | 'suspended'
  | 'printer_state_changed'
  | 'job_failed'
  | 'late_ack'
  | 'protocol_refused'
  | 'update_progress'
  | 'update_finished'
  | 'connection_test';

export interface TenantPrintingAgent {
  id: string;
  name: string;
  agentVersion: string | null;
  protocolVersion: number | null;
  /** Se a API ainda aceita o protocolo do computador (change print-agent). */
  protocolSupported: boolean;
  /** Nível pela política do canal da loja; sempre `none` no revogado. */
  updateLevel: AgentUpdateLevel;
  /** O pedido de atualização aberto, ou o último desfecho, sem quem confirmou no painel. */
  lastUpdateRequest: PlatformAgentUpdateRequest | null;
  status: PrintAgentStatus;
  online: boolean;
  lastSeenAt: string | null;
  pairedAt: string;
  revokedAt: string | null;
  revokeReason: PrintAgentRevokeReason | null;
}

export interface TenantPrintingPrinter {
  id: string;
  displayName: string;
  origin: PrinterOrigin;
  transport: PrinterTransport;
  width: PrinterWidth;
  state: PrinterState;
  stateChangedAt: string | null;
  agentId: string | null;
  available: boolean;
  lastError: { code: PrintJobErrorCode; at: string } | null;
}

export interface TenantPrintingResponse {
  tenantId: string;
  flags: { pilot: boolean; platformEnabled: boolean; availability: PrintingAvailability };
  accessBlocked: boolean;
  setup: {
    activation: PrintingActivation;
    activatedAt: string | null;
    deactivatedAt: string | null;
    paused: boolean;
    pausedAt: string | null;
    resumedAt: string | null;
  };
  /** Canal de atualização do agente da loja (change print-agent). */
  agentChannel: TenantAgentChannel;
  agents: TenantPrintingAgent[];
  printers: TenantPrintingPrinter[];
  stations: { count: number; withoutPrinter: number };
  ticketsByPrinter: Array<{
    printerId: string;
    printerName: string;
    tickets: Array<{ kind: PrintTicketKind; stationName: string | null; copies: number }>;
  }>;
  recentEvents: Array<{
    type: PrintAgentEventType;
    code: string | null;
    occurredAt: string;
    agentId: string | null;
    printerId: string | null;
    jobId: string | null;
  }>;
}

/**
 * Impressão de uma loja, para o suporte. Só leitura e sem conteúdo de pedido: o
 * máximo que sai é o id do trabalho no evento do computador. Tenant inativo
 * responde 422, como as outras leituras por tenant.
 */
export async function getTenantPrinting(tenantId: string): Promise<TenantPrintingResponse> {
  const { data } = await api.get<TenantPrintingResponse>(`/platform/tenants/${tenantId}/printing`);
  return data;
}

/* ---- Agente de impressão: versões e canal (change print-agent) ---- */

/** Canal de atualização: o piloto recebe primeiro cada versão; o estável, só as promovidas. */
export type AgentUpdateChannel = 'pilot' | 'stable';
/** `mandatory` abaixo da mínima, `recommended` abaixo da recomendada, `none` nos demais casos. */
export type AgentUpdateLevel = 'none' | 'recommended' | 'mandatory';
export type AgentUpdateStatus = 'requested' | 'in_progress' | 'succeeded' | 'failed' | 'expired';
export type AgentUpdateMode = 'now' | 'end_of_day';
export type AgentUpdatePhase = 'waiting_for_empty_queue' | 'downloading' | 'applying';
export type AgentUpdateFailureCode =
  | 'download_failed'
  | 'verification_failed'
  | 'apply_failed'
  | 'version_not_in_feed'
  | 'apply_timeout';
/** `unavailable`: a última leitura do manifesto do canal falhou; `not_configured`: ambiente sem feed. */
/**
 * `ok` também com o manifesto do canal ainda não gravado (canal sem versão, como o estável antes
 * da primeira promoção); `unavailable` é falha de leitura (rede, 5xx, JSON fora do formato).
 */
export type AgentFeedState = 'ok' | 'unavailable' | 'not_configured';

/** Um pedido de atualização visto pelo suporte: alvo e canal congelados, fase, falha e horários. */
export interface PlatformAgentUpdateRequest {
  id: string;
  status: AgentUpdateStatus;
  mode: AgentUpdateMode;
  targetVersion: string;
  channel: AgentUpdateChannel;
  notBefore: string;
  phase: AgentUpdatePhase | null;
  failureCode: AgentUpdateFailureCode | null;
  requestedAt: string;
  finishedAt: string | null;
}

/** O canal da loja; `stable` com `updatedAt` nulo quando nunca houve escolha. */
export interface TenantAgentChannel {
  channel: AgentUpdateChannel;
  updatedAt: string | null;
  updatedByOperatorId: string | null;
}

export interface TenantAgentChannelResponse extends TenantAgentChannel {
  tenantId: string;
}

/** Uma versão do manifesto do canal, publicada pelo pipeline. */
export interface AgentReleaseVersion {
  version: string;
  publishedAt: string;
  signed: boolean;
  /** Se a API aceita a versão como mínima ou recomendada deste canal. */
  selectable: boolean;
}

/** A política de um canal, com o manifesto e a frota dele (só contagens). */
export interface AgentReleaseChannel {
  channel: AgentUpdateChannel;
  minimumVersion: string | null;
  recommendedVersion: string | null;
  updatedAt: string;
  /** Nulo na linha semeada pela migration. */
  updatedByOperatorId: string | null;
  /** Devolvido no PUT; outra gravação no meio responde 409. */
  concurrencyToken: number;
  feed: { state: AgentFeedState; readAt: string | null };
  /** Da mais nova para a mais antiga. */
  versions: AgentReleaseVersion[];
  agentVersions: Array<{ version: string | null; agents: number }>;
  protocolVersions: Array<{ protocolVersion: number | null; agents: number }>;
  /**
   * Dos mesmos computadores, os de protocolo recusado pela API, por versão do agente. A API os
   * torna `mandatory` sempre que houver versão-alvo maior que a deles, mesmo acima da mínima.
   */
  refusedProtocolAgentVersions: Array<{ version: string | null; agents: number }>;
}

export interface AgentReleasesResponse {
  /** O protocolo que a API fala e a mínima que ela aceita. */
  protocol: { current: number; minimum: number };
  /** `pilot` e `stable`, nessa ordem. */
  channels: AgentReleaseChannel[];
}

export interface UpdateAgentReleasePolicyPayload {
  minimumVersion: string | null;
  recommendedVersion: string | null;
  concurrencyToken: number;
}

/**
 * Versões do agente por canal: mínima e recomendada, o manifesto do feed e a frota de cada
 * canal em contagens. Global por design, sem tenant no caminho; qualquer operador lê.
 */
export async function getAgentReleases(): Promise<AgentReleasesResponse> {
  const { data } = await api.get<AgentReleasesResponse>('/platform/printing/releases');
  return data;
}

/**
 * Grava a mínima e a recomendada de um canal. Só `Admin` (403 para os demais). 409 quando
 * outro administrador gravou depois da leitura; 503 `printing.agent_feed_unavailable` quando o
 * manifesto do canal não pôde ser lido para conferir as versões.
 */
export async function updateAgentReleasePolicy(
  channel: AgentUpdateChannel,
  payload: UpdateAgentReleasePolicyPayload,
): Promise<AgentReleaseChannel> {
  const { data } = await api.put<AgentReleaseChannel>(`/platform/printing/releases/${channel}`, payload);
  return data;
}

/** Canal do agente de uma loja; o tenant vem só do caminho. */
export async function getTenantAgentChannel(tenantId: string): Promise<TenantAgentChannelResponse> {
  const { data } = await api.get<TenantAgentChannelResponse>(`/platform/tenants/${tenantId}/printing/channel`);
  return data;
}

/**
 * Troca o canal do agente da loja. Só `Admin`, e a troca é auditada. Gravar o canal em que a
 * loja já está responde 200 sem gravar.
 */
export async function setTenantAgentChannel(
  tenantId: string,
  channel: AgentUpdateChannel,
): Promise<TenantAgentChannelResponse> {
  const { data } = await api.put<TenantAgentChannelResponse>(`/platform/tenants/${tenantId}/printing/channel`, {
    channel,
  });
  return data;
}

/** Status HTTP de um erro de requisição, ou nulo quando não houve resposta. */
export function problemStatus(err: unknown): number | null {
  return err instanceof AxiosError ? (err.response?.status ?? null) : null;
}

/** O `errorCode` de um ProblemDetails, quando a API manda um. */
export function problemErrorCode(err: unknown): string | null {
  if (!(err instanceof AxiosError)) return null;
  const data = err.response?.data as { errorCode?: unknown } | undefined;
  return typeof data?.errorCode === 'string' ? data.errorCode : null;
}

/**
 * Unidade de `limit` e `requested` no 422 `platform.commercial.limit_exceeded` (C2 da change
 * platform-backoffice-hardening). A API manda a unidade; a tela informa a dela para o caso de
 * uma resposta sem o campo.
 */
export type CommercialLimitUnit = 'days' | 'currency' | 'percent' | 'months' | 'active_extensions';

const COMMERCIAL_LIMIT_UNITS: readonly CommercialLimitUnit[] = [
  'days',
  'currency',
  'percent',
  'months',
  'active_extensions',
];

function formatCommercialQuantity(value: number, unit: CommercialLimitUnit): string {
  const number = value.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  switch (unit) {
    case 'currency':
      return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case 'percent':
      return `${number}%`;
    case 'days':
      return value === 1 ? '1 dia' : `${number} dias`;
    case 'months':
      return value === 1 ? '1 mês' : `${number} meses`;
    case 'active_extensions':
      return value === 1 ? '1 extensão vigente' : `${number} extensões vigentes`;
  }
}

/**
 * Mensagem dos erros das ações comerciais (extensão de trial, ajuste, desconto e concessão):
 * o teto mostra o pedido e o limite na unidade da tela, e o piso da extensão explica que
 * encurtar é cancelar. Os demais erros seguem `describeError`.
 *
 * `canOverride` é o papel de quem vê a tela (só `Admin` passa do teto, com a exceção marcada): na
 * extensão vigente, o Admin também ouve que a exceção resolve, em vez de só "cancele a vigente".
 */
export function describeCommercialError(
  err: unknown,
  fallbackUnit: CommercialLimitUnit = 'days',
  { canOverride = false }: { canOverride?: boolean } = {},
): string {
  const code = problemErrorCode(err);

  if (code === 'platform.commercial.extension_shortens') {
    return 'A nova data encurta o trial; para encurtar, cancele a extensão.';
  }

  // C13 (N6): o crédito passa do valor ao vivo da fatura; a API manda o valor dela.
  if (code === 'platform.commercial.credit_exceeds_invoice' && err instanceof AxiosError) {
    const data = err.response?.data as { invoiceAmount?: unknown } | undefined;
    return typeof data?.invoiceAmount === 'number'
      ? `O crédito passa do valor da fatura no Asaas (${formatCommercialQuantity(data.invoiceAmount, 'currency')}).`
      : 'O crédito passa do valor da fatura no Asaas.';
  }

  if (code === 'platform.commercial.discount_overlap') {
    return 'A assinatura já tem um desconto pendente ou ativo. Revogue-o antes de criar outro.';
  }

  if (code === 'platform.commercial.limit_exceeded' && err instanceof AxiosError) {
    const data = err.response?.data as { limit?: unknown; requested?: unknown; unit?: unknown } | undefined;
    const unit = COMMERCIAL_LIMIT_UNITS.find((u) => u === data?.unit) ?? fallbackUnit;
    const limit = typeof data?.limit === 'number' ? data.limit : null;
    const requested = typeof data?.requested === 'number' ? data.requested : null;
    if (limit === null || requested === null) {
      return 'Acima do teto comercial. Acima dele, só um Admin, com a exceção marcada.';
    }
    const message =
      `Acima do teto: pedido ${formatCommercialQuantity(requested, unit)}, `
      + `limite ${formatCommercialQuantity(limit, unit)}.`;
    if (unit !== 'active_extensions') return message;
    return canOverride
      ? `${message} Cancele a extensão vigente antes de estender de novo, ou marque a exceção de Admin.`
      : `${message} Cancele a extensão vigente antes de estender de novo.`;
  }

  return describeError(err);
}

/**
 * Como `describeError`, mas prefere a mensagem do campo num 400 de validação: o `title` desse
 * formato é o genérico em inglês, e o que explica o erro está em `errors`.
 */
export function describeFieldError(err: unknown): string {
  if (err instanceof AxiosError) {
    const errors = (err.response?.data as { errors?: Record<string, string[]> } | undefined)?.errors;
    const first = errors
      ? Object.values(errors).find((messages) => Array.isArray(messages) && messages.length > 0)
      : undefined;
    if (first) return first[0];
  }
  return describeError(err);
}

// -----------------------------------------------------------------------------
//  Recusas do login e do segundo fator (change platform-backoffice-hardening, C5)
// -----------------------------------------------------------------------------

/** `errorCode` do 423 da conta bloqueada por falhas de segundo fator. */
export const AUTH_LOCKED_ERROR_CODE = 'platform.auth.locked';

/**
 * Segundos de espera de uma recusa do login: a extensão `retryAfter` do ProblemDetails ou, sem
 * ela, o cabeçalho `Retry-After` (em segundos, como a API manda). Nulo quando não há nenhum dos
 * dois ou o valor não é um número positivo.
 */
export function problemRetryAfterSeconds(err: unknown): number | null {
  if (!(err instanceof AxiosError)) return null;
  const data = err.response?.data as { retryAfter?: unknown } | undefined;
  if (typeof data?.retryAfter === 'number' && Number.isFinite(data.retryAfter) && data.retryAfter > 0) {
    return Math.ceil(data.retryAfter);
  }
  const headers = err.response?.headers as Record<string, unknown> | undefined;
  const raw = headers?.['retry-after'] ?? headers?.['Retry-After'];
  const seconds = typeof raw === 'string' || typeof raw === 'number' ? Number(raw) : Number.NaN;
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : null;
}

/**
 * Mensagem da conta bloqueada (423 `platform.auth.locked`), com a espera em minutos arredondada
 * para cima. Nulo para qualquer outro erro.
 */
export function authLockMessage(err: unknown): string | null {
  if (problemStatus(err) !== 423 || problemErrorCode(err) !== AUTH_LOCKED_ERROR_CODE) return null;
  const seconds = problemRetryAfterSeconds(err);
  const wait = seconds ? `Tente de novo em ${Math.max(1, Math.ceil(seconds / 60))} min` : 'Tente de novo mais tarde';
  return `Conta bloqueada por excesso de tentativas. ${wait} ou peça o desbloqueio a um administrador.`;
}

/**
 * Mensagem das telas de login, MFA e troca de senha: a conta bloqueada (423), o excesso de
 * tentativas (429, do freio por email e IP ou do limite por IP) e, nos demais casos, o
 * `describeError` de sempre.
 */
export function describeAuthRefusal(err: unknown): string {
  const lock = authLockMessage(err);
  if (lock) return lock;
  if (problemStatus(err) === 429) {
    const seconds = problemRetryAfterSeconds(err);
    return seconds
      ? `Muitas tentativas. Aguarde ${seconds} s e tente de novo.`
      : 'Muitas tentativas. Aguarde e tente de novo.';
  }
  return describeError(err);
}

// =============================================================================
//  Agentes do MCP: clientes por perfil, concessões e uso (change platform-mcp-server, D28)
// =============================================================================
//
// Rotas globais e só para Admin, com `PlatformJwt` (o mesmo `api` das outras rotas platform).
// Nenhuma usa `Idempotency-Key`: a concorrência vai pelo `expectedGeneration`, e a segunda
// requisição com a mesma geração responde 409 `generation_mismatch` sem efeito. Nenhuma
// resposta traz token, code ou hash; mesmo assim, nada daqui vai para log nem para storage.

/** Escopos do MCP, hierárquicos: `metrics` < `stores` < `billing`. */
export type AgentScope =
  | 'platform.mcp.metrics.read'
  | 'platform.mcp.stores.read'
  | 'platform.mcp.billing.read';

/** Motivo da lista fechada da gestão (`PlatformAgentTextLists.ManagementReason`). */
export type AgentManagementReason =
  | 'scheduled'
  | 'suspected_leak'
  | 'owner_change'
  | 'scope_change'
  | 'other';

/** `PlatformAgentTextLists.ClientStatus`. */
export type AgentClientStatus = 'Enabled' | 'Disabled';

/** `PlatformAgentTextLists.GrantStatus`; é também o filtro `status` da listagem. */
export type AgentGrantStatus = 'Active' | 'Revoked' | 'Expired';

/** `PlatformAgentTextLists.RevokeReason`: por que a concessão saiu de uso. */
export type AgentGrantRevokeReason =
  | 'admin'
  | 'superseded'
  | 'refresh_reuse'
  | 'code_reuse'
  | 'client_disabled'
  | 'owner_revoked'
  | 'owner_demoted'
  | 'owner_reset'
  | 'owner_reconciled'
  | 'manual_sql'
  | 'pepper_rotated'
  // Cascata pela conta titular do cliente `Operator` (OA-D5).
  | 'subject_revoked'
  | 'subject_demoted'
  | 'subject_reset'
  | 'subject_locked';

/** `PlatformAgentTextLists.ToolCallOutcome`. */
export type AgentToolCallOutcome =
  | 'ok'
  | 'business_error'
  | 'scope_missing'
  | 'cap_reached'
  | 'unknown_tool'
  | 'timeout'
  | 'cancelled'
  | 'unavailable'
  | 'exception';

/** A concessão ativa como a lista de clientes a mostra (`AgentActiveGrantDto`). */
export interface AgentActiveGrant {
  id: string;
  scopes: AgentScope[];
  /** Interseção da concessão, do teto do cliente e do `AllowedScopes` do ambiente. */
  effectiveScopes: AgentScope[];
  approvedByAccountId: string;
  approvedByName: string | null;
  approvedAt: string;
  expiresAt: string;
  refreshIdleExpiresAt: string;
  lastUsedAt: string | null;
  refreshGeneration: number;
  catalogVersion: number;
  generation: number;
}

/**
 * Um cliente OAuth do MCP (`AgentClientDto`): o perfil do Hermes ou, com a change
 * platform-mcp-operator-access, o cliente pessoal de um operador (`clientKind = Operator`).
 */
export interface AgentClient extends AgentClientSubjectFields {
  id: string;
  oauthClientId: string;
  environmentTag: string;
  profileName: string;
  redirectUri: string;
  redirectHost: string;
  /** O teto vigente: leitura e, num perfil de `WriteProfiles`, a escrita reservada (1.18). */
  maxScopes: (AgentScope | AgentWriteScope)[];
  status: AgentClientStatus;
  registeredAt: string;
  certifiedAt: string;
  certificationDueAt: string;
  disabledAt: string | null;
  disabledReason: AgentManagementReason | null;
  generation: number;
  activeGrant: AgentActiveGrant | null;
  /**
   * "Teto ampliado com espera" (change platform-mcp-writes, 1.18): o teto pedido com escrita, que
   * só vale em `pendingMaxScopesEffectiveAt`. Nulos sem subida pendente.
   */
  pendingMaxScopes?: (AgentScope | AgentWriteScope)[] | null;
  pendingMaxScopesEffectiveAt?: string | null;
}

/** Uma concessão, ativa ou histórico (`AgentGrantDto`). */
export interface AgentGrant {
  id: string;
  agentClientId: string;
  oauthClientId: string;
  profileName: string;
  status: AgentGrantStatus;
  scopes: AgentScope[];
  catalogVersion: number;
  approvedByAccountId: string;
  approvedByName: string | null;
  approvedAt: string;
  issuedAt: string;
  expiresAt: string;
  refreshIdleExpiresAt: string;
  refreshGeneration: number;
  generation: number;
  revokedAt: string | null;
  revokeReason: AgentGrantRevokeReason | null;
  /** `subject_self_revoked`: o próprio titular revogou a concessão pessoal (tarefa 4.3). */
  managementReason: AgentManagementReason | 'subject_self_revoked' | null;
  revokedByAccountId: string | null;
  replacesGrantId: string | null;
  lastUsedAt: string | null;
  lastRefreshAt: string | null;
  /** Propostas pendentes criadas por esta concessão (change platform-mcp-writes, 1.22). */
  pendingProposals?: number;
}

/** Uma chamada da trilha de uso (`AgentToolCallDto`): sem argumentos, resposta nem IP. */
export interface AgentToolCall {
  id: string;
  calledAt: string;
  toolName: string;
  outcome: AgentToolCallOutcome;
  errorCode: string | null;
  durationMs: number;
  itemCount: number;
  resultChars: number;
  cacheHit: boolean;
  refreshGeneration: number;
  /** A loja-alvo só como id curto (8 hex). */
  targetTenantShortId: string | null;
  clientProfile: string;
  /** Declarado pelo próprio cliente: a tela mostra como texto rotulado, nunca como HTML. */
  clientUserAgent: string;
  protocolVersion: string | null;
}

export interface AgentToolCallPage {
  items: AgentToolCall[];
  nextCursor: string | null;
}

export interface RegisterAgentClientPayload {
  profileName: string;
  redirectUri: string;
  maxScopes: AgentScope[];
  reason: AgentManagementReason;
  detail?: string;
}

export interface ChangeAgentClientCeilingPayload {
  expectedGeneration: number;
  maxScopes: (AgentScope | AgentWriteScope)[];
  reason: AgentManagementReason;
  detail?: string;
}

export interface ChangeAgentClientRedirectUriPayload {
  expectedGeneration: number;
  redirectUri: string;
  reason: AgentManagementReason;
  detail?: string;
}

export interface DisableAgentClientPayload {
  expectedGeneration: number;
  reason: AgentManagementReason;
  detail?: string;
}

export interface NarrowAgentGrantPayload {
  expectedGeneration: number;
  scopes: AgentScope[];
}

export interface RevokeAgentGrantPayload {
  /** Opcional na API; a página sempre manda o que leu. */
  expectedGeneration?: number;
  reason: AgentManagementReason;
  detail?: string;
}

const AGENT_CLIENTS = '/platform/agent-clients';
const AGENT_GRANTS = '/platform/agent-grants';

/** Detalhe vazio não vai no corpo: a API trata ausente e vazio igual, e o corpo fica limpo. */
function withOptionalDetail<T extends { detail?: string }>(payload: T): T {
  const detail = payload.detail?.trim();
  return { ...payload, detail: detail ? detail : undefined };
}

/** Clientes do ambiente, por perfil, com a concessão ativa. */
export async function listAgentClients(): Promise<AgentClient[]> {
  const { data } = await api.get<AgentClient[]>(AGENT_CLIENTS);
  return data;
}

/** Registra o cliente de um perfil; exige login recente por senha e TOTP. */
export async function registerAgentClient(payload: RegisterAgentClientPayload): Promise<AgentClient> {
  const { data } = await api.post<AgentClient>(AGENT_CLIENTS, withOptionalDetail(payload));
  return data;
}

/**
 * Muda o teto do perfil; baixar reduz a concessão ativa, subir não a amplia. Corpo com escopo de
 * escrita (1.18, "Teto ampliado com espera") vai com a passkey presa ao cliente
 * (`agent_client.ceiling_write`); a subida com escrita responde 202, com o teto em
 * `pendingMaxScopes` até `pendingMaxScopesEffectiveAt`.
 */
export async function changeAgentClientCeiling(
  id: string,
  payload: ChangeAgentClientCeilingPayload,
  stepUp?: StepUpProof,
): Promise<AgentClient> {
  const { data } = await api.post<AgentClient>(
    `${AGENT_CLIENTS}/${encodeURIComponent(id)}/ceiling`,
    withOptionalDetail(payload),
    ...(stepUp === undefined ? [] : [{ headers: stepUpHeaders(stepUp) }]),
  );
  return data;
}

/**
 * Troca a URL de volta (`redirect_uri`) do perfil, com o mesmo `client_id`; exige login recente por
 * senha e TOTP. A concessão vigente continua até o próximo Authenticate pelo endereço novo.
 */
export async function changeAgentClientRedirectUri(
  id: string,
  payload: ChangeAgentClientRedirectUriPayload,
): Promise<AgentClient> {
  const { data } = await api.post<AgentClient>(
    `${AGENT_CLIENTS}/${encodeURIComponent(id)}/redirect-uri`,
    withOptionalDetail(payload),
  );
  return data;
}

/** Desabilita o perfil e revoga a concessão ativa: a próxima chamada do agente recebe 401. */
export async function disableAgentClient(id: string, payload: DisableAgentClientPayload): Promise<AgentClient> {
  const { data } = await api.post<AgentClient>(
    `${AGENT_CLIENTS}/${encodeURIComponent(id)}/disable`,
    withOptionalDetail(payload),
  );
  return data;
}

/** Habilita de novo o perfil; exige login recente por senha e TOTP. */
export async function enableAgentClient(id: string, payload: { expectedGeneration: number }): Promise<AgentClient> {
  const { data } = await api.post<AgentClient>(`${AGENT_CLIENTS}/${encodeURIComponent(id)}/enable`, {
    expectedGeneration: payload.expectedGeneration,
  });
  return data;
}

/** Recertifica o perfil (`certifiedAt = agora`); exige login recente por senha e TOTP. */
export async function recertifyAgentClient(
  id: string,
  payload: { expectedGeneration: number },
): Promise<AgentClient> {
  const { data } = await api.post<AgentClient>(`${AGENT_CLIENTS}/${encodeURIComponent(id)}/recertify`, {
    expectedGeneration: payload.expectedGeneration,
  });
  return data;
}

/** Concessões do ambiente, ativas e histórico (as 200 mais recentes). */
export async function listAgentGrants(
  filters: { clientId?: string; status?: AgentGrantStatus } = {},
): Promise<AgentGrant[]> {
  const { data } = await api.get<AgentGrant[]>(AGENT_GRANTS, {
    params: { clientId: filters.clientId || undefined, status: filters.status || undefined },
  });
  return data;
}

/** Reduz a concessão a um subconjunto estrito e hierárquico dos escopos dela. */
export async function narrowAgentGrant(id: string, payload: NarrowAgentGrantPayload): Promise<AgentGrant> {
  const { data } = await api.post<AgentGrant>(`${AGENT_GRANTS}/${encodeURIComponent(id)}/narrow`, {
    expectedGeneration: payload.expectedGeneration,
    scopes: payload.scopes,
  });
  return data;
}

/** Revoga a concessão (terminal; revogar de novo responde 200 sem transição). */
export async function revokeAgentGrant(id: string, payload: RevokeAgentGrantPayload): Promise<AgentGrant> {
  const { data } = await api.post<AgentGrant>(
    `${AGENT_GRANTS}/${encodeURIComponent(id)}/revoke`,
    withOptionalDetail(payload),
  );
  return data;
}

/**
 * "Suspender escritas" (change platform-mcp-writes, 1.22): a concessão fica só com os escopos de
 * leitura; a próxima escrita do agente recebe `scope_missing`. As propostas pendentes ficam.
 */
export async function suspendAgentGrantWrites(id: string, payload: { expectedGeneration: number }): Promise<AgentGrant> {
  const { data } = await api.post<AgentGrant>(`${AGENT_GRANTS}/${encodeURIComponent(id)}/suspend-writes`, {
    expectedGeneration: payload.expectedGeneration,
  });
  return data;
}

export interface RevokeAllWriteGrantsResult {
  revokedGrants: number;
  cancelledProposals: number;
}

/**
 * "Revogar todas as delegações de escrita" (1.22): contenção de incidente, numa transação; as
 * propostas pendentes do ambiente vão a `CancelledByRevocation`. As concessões só de leitura ficam.
 */
export async function revokeAllWriteGrants(payload: {
  reason: AgentManagementReason;
  detail?: string;
}): Promise<RevokeAllWriteGrantsResult> {
  const { data } = await api.post<RevokeAllWriteGrantsResult>(`${AGENT_GRANTS}/revoke-writes`, withOptionalDetail(payload));
  return data;
}

/** Uso de uma concessão, do mais novo para o mais antigo, 50 por página. Toda leitura é auditada. */
export async function listAgentToolCalls(
  grantId: string,
  options: { days?: number; cursor?: string | null } = {},
): Promise<AgentToolCallPage> {
  const { data } = await api.get<AgentToolCallPage>(
    `${AGENT_GRANTS}/${encodeURIComponent(grantId)}/tool-calls`,
    { params: { days: options.days ?? undefined, cursor: options.cursor || undefined } },
  );
  return data;
}

// =============================================================================
//  Consentimento de concessão do MCP (change platform-mcp-server, D28, passos 7 a 9)
// =============================================================================
//
// Tipos de `PlatformAgentAuthorizationRequests.cs`. Rotas só para Admin, com `PlatformJwt` e sem
// `Idempotency-Key`: o filtro guardaria a resposta do approve, que carrega o code. A resposta do
// approve e do deny é só a `redirectUrl` montada pelo servidor; a página faz `location.assign`
// dela e não a guarda em estado, storage nem log. O approve leva o step-up por TOTP
// (`X-Platform-Step-Up`, tarefa 7.3 da change platform-backoffice-hardening) e mais nada: sem
// `Idempotency-Key` e sem `sensitiveWriteHeaders`, porque a idempotência dele vem do status do
// pedido na API.

/** Motivo de um escopo pedido que ficou fora do concedível (`PlatformAgentAuthorizationRequestValidator`). */
export type AgentAuthorizationExclusionReason =
  | 'above_ceiling'
  | 'not_allowed_in_environment'
  | 'offline_access_discarded';

/** Um escopo concedível, com o rótulo em pt-BR do que ele libera (`AgentAuthorizationScopeDto`). */
export interface AgentAuthorizationScope {
  scope: AgentScope;
  label: string;
}

/** Um escopo pedido que ficou de fora, com o motivo (`AgentAuthorizationExcludedScopeDto`). */
export interface AgentAuthorizationExcludedScope {
  /** Pode ser um escopo fora da lista do MCP, como `offline_access`. */
  scope: string;
  reason: AgentAuthorizationExclusionReason;
}

/** A concessão vigente do perfil, que a troca do code vai substituir (`AgentAuthorizationCurrentGrantDto`). */
export interface AgentAuthorizationCurrentGrant {
  id: string;
  scopes: AgentScope[];
  expiresAt: string;
  approvedByAccountId: string;
  approvedByName: string;
}

/**
 * O que a tela de consentimento mostra (`AgentAuthorizationConsentDto`, `kind = consent`): só valores
 * validados pelo servidor, nenhum parâmetro cru da query.
 */
export interface AgentAuthorizationConsent {
  kind: 'consent';
  requestId: string;
  profile: string;
  environmentTag: string;
  oauthClientId: string;
  redirectHost: string;
  requestedAt: string;
  expiresAt: string;
  /** Em ordem de nível; pode trazer `offline_access`, que sai em `excludedScopes`. */
  requestedScopes: string[];
  /** Pedido ∩ teto do perfil ∩ `AllowedScopes` do ambiente, em ordem de nível. */
  grantableScopes: AgentAuthorizationScope[];
  excludedScopes: AgentAuthorizationExcludedScope[];
  grantMaxDays: number;
  grantExpiresAt: string;
  currentGrant: AgentAuthorizationCurrentGrant | null;
  catalogVersion: number;
  /**
   * Ausente na API anterior: vale como perfil do Hermes. `Operator` abre a variante pessoal. O DTO
   * não traz o titular: a API só grava o pedido pessoal da própria conta titular (403
   * `request_not_yours` para qualquer outra, Admin inclusive).
   */
  clientKind?: AgentClientKind;
  /** Janela de presença do refresh pessoal, em horas (OA-D4); nulo no perfil do Hermes. */
  presenceHours?: number | null;
  /**
   * A escrita (change platform-mcp-writes, tarefa 1.18): presente só para o perfil que pode escrever
   * neste ambiente. Sem intenção preparada em `/agentes`, `prepared` é falso e a tela mostra só a
   * leitura, com o aviso.
   */
  write?: AgentAuthorizationWrite | null;
  /**
   * Para onde a concessão pessoal volta (change platform-mcp-operator-access, OA-D12), decidido pelo
   * servidor a partir da `redirect_uri` validada: `loopback` (o Claude Code neste computador) ou
   * `claude_web` (o conector do claude.ai). Só no cliente `Operator`; ausente na API anterior.
   */
  redirectTarget?: PersonalRedirectTarget | null;
}

/** O destino da volta do consentimento pessoal (`PlatformAgentClientRules.RedirectTargetOf`). */
export type PersonalRedirectTarget = 'loopback' | 'claude_web';

/** Um escopo de escrita do catálogo (`PlatformMcpWriteScopes.Reserved`). */
export type AgentWriteScope =
  | 'platform.mcp.trial.propose'
  | 'platform.mcp.trial.write'
  | 'platform.mcp.concessions.propose'
  | 'platform.mcp.concessions.write'
  | 'platform.mcp.plans.propose'
  | 'platform.mcp.notes.write'
  | 'platform.mcp.adjustments.propose'
  | 'platform.mcp.discounts.propose'
  | 'platform.mcp.full';

/** Um escopo de escrita com o rótulo em pt-BR (`AgentAuthorizationScopeDto`). */
export interface AgentWriteScopeItem {
  scope: AgentWriteScope;
  label: string;
}

/** O controle total preparado (`AgentAuthorizationFullControlDto`). */
export interface AgentAuthorizationFullControl {
  offered: boolean;
  /** `profile_not_allowed`, `intent_too_old` ou `nothing_grantable`. */
  refusal: string | null;
  /** A lista expandida que ficaria congelada na delegação. */
  scopes: AgentWriteScopeItem[];
}

/** A seção de escrita da tela de consentimento (`AgentAuthorizationWriteDto`). */
export interface AgentAuthorizationWrite {
  prepared: boolean;
  notice: string | null;
  intentId: string | null;
  intentCreatedAt: string | null;
  intentExpiresAt: string | null;
  secondsSinceIntent: number | null;
  preparedScopes: AgentWriteScopeItem[];
  grantableScopes: AgentWriteScopeItem[];
  excludedScopes: AgentAuthorizationExcludedScope[];
  /** Os concedíveis de risco Alto: uma caixa cada e passkey. */
  highRiskScopes: AgentWriteScope[];
  fullControl: AgentAuthorizationFullControl | null;
}

/**
 * Erro depois da `redirect_uri` (`AgentAuthorizationRedirectErrorDto`, `kind = redirect`): a página
 * mostra o motivo e manda o navegador para a `redirectUrl` do servidor, com `error`, `state` e `iss`.
 */
export interface AgentAuthorizationRedirectError {
  kind: 'redirect';
  error: string;
  redirectUrl: string;
}

export type AgentAuthorizationCreateResponse = AgentAuthorizationConsent | AgentAuthorizationRedirectError;

/** Resposta do approve e do deny (`AgentAuthorizationRedirectDto`): só a URL de volta ao Hermes. */
export interface AgentAuthorizationRedirect {
  redirectUrl: string;
}

export interface ApproveAgentAuthorizationPayload {
  /**
   * O conjunto cumulativo do nível escolhido (o nível 2 é `metrics` e `stores`), nunca acima do
   * concedível, seguido da escrita marcada entre a preparada (platform-mcp-writes, 1.18).
   */
  scopes: (AgentScope | AgentWriteScope)[];
  /** O `catalogVersion` que a tela mostrou; outro na API dá 409 `catalog_changed`. */
  catalogVersion: number;
  /** Cada escopo de risco Alto aprovado, confirmado na sua caixa. */
  confirmedScopes?: AgentWriteScope[];
  /** O nome do perfil digitado, exigido só para o controle total. */
  profileConfirmation?: string;
}

const AGENT_AUTHORIZATIONS = '/platform/agent-authorizations';
/**
 * Variante pessoal (change platform-mcp-operator-access, OA-D3): mesmos corpos e respostas, para a
 * conta titular de qualquer papel, só com `client_id` de operador e com o step-up de propósito
 * `operator_authorization.approve`. A rota do agente é só de Admin.
 */
const OPERATOR_AUTHORIZATIONS = '/platform/operator-authorizations';

/** Qual rota de consentimento: a do agente do Hermes (só Admin) ou a pessoal do operador. */
export type AuthorizationRoute = 'agent' | 'operator';

function authorizationsPrefix(route: AuthorizationRoute): string {
  return route === 'operator' ? OPERATOR_AUTHORIZATIONS : AGENT_AUTHORIZATIONS;
}

/**
 * Grava o pedido de autorização com a query do `/authorize` inteira, sem interpretação (a API valida
 * tudo), e devolve a tela de consentimento ou o erro que volta ao cliente.
 */
export async function createAgentAuthorization(
  payload: { query: string },
  route: AuthorizationRoute = 'agent',
): Promise<AgentAuthorizationCreateResponse> {
  const { data } = await api.post<AgentAuthorizationCreateResponse>(authorizationsPrefix(route), {
    query: payload.query,
  });
  return data;
}

/**
 * Aprova o pedido no nível escolhido, com o código TOTP atual do Admin em `X-Platform-Step-Up`
 * (o modal de step-up o pede no Aprovar); a `redirectUrl` leva code, `state` e `iss`. Só esse
 * cabeçalho: nada de `Idempotency-Key`, que a API não usa nesta rota.
 */
export async function approveAgentAuthorization(
  id: string,
  payload: ApproveAgentAuthorizationPayload,
  stepUp: StepUpProof,
  route: AuthorizationRoute = 'agent',
): Promise<AgentAuthorizationRedirect> {
  const { data } = await api.post<AgentAuthorizationRedirect>(
    `${authorizationsPrefix(route)}/${encodeURIComponent(id)}/approve`,
    {
      scopes: payload.scopes,
      catalogVersion: payload.catalogVersion,
      ...(payload.confirmedScopes ? { confirmedScopes: payload.confirmedScopes } : {}),
      ...(payload.profileConfirmation !== undefined ? { profileConfirmation: payload.profileConfirmation } : {}),
    },
    { headers: stepUpHeaders(stepUp) },
  );
  return data;
}

/** Nega o pedido; a `redirectUrl` leva `error=access_denied`, `state` e `iss`. */
export async function denyAgentAuthorization(
  id: string,
  route: AuthorizationRoute = 'agent',
): Promise<AgentAuthorizationRedirect> {
  const { data } = await api.post<AgentAuthorizationRedirect>(
    `${authorizationsPrefix(route)}/${encodeURIComponent(id)}/deny`,
    {},
  );
  return data;
}

// =============================================================================
//  Operador no Claude Code (change platform-mcp-operator-access, PR 8.3)
// =============================================================================
//
// O cliente `Operator` é um por conta e por ambiente (OA-D1), habilitado por um Admin em
// `/agentes` e consentido só pelo titular em `/mcp/autorizar`, com step-up (OA-D3). O contrato é o
// da PR 8.1: os campos do titular no `AgentClientDto`, `POST /platform/agent-clients/operators` e
// as rotas `operator-authorizations`. O estado do caminho do operador (`PlatformMcp:Operator:Enabled`)
// vem de `GET /platform/me/operator-access` (tarefa 4.3); desligado, a habilitação ainda responde
// `409 operator_disabled` e o pedido pessoal, `400 invalid_client`. Nenhuma resposta traz token,
// code ou hash.
//
// Os campos novos dos DTOs existentes são opcionais: a API anterior não os manda, e ausente vale
// como cliente de perfil do Hermes.

/** Tipo do cliente OAuth (`ClientKind`, OA-D1). `Machine` fica reservado, sem tela. */
export type AgentClientKind = 'AgentProfile' | 'Operator' | 'Machine';

/** Campos do titular no `AgentClientDto` (só no cliente `Operator`; o papel não vem). */
export interface AgentClientSubjectFields {
  clientKind?: AgentClientKind;
  subjectAccountId?: string | null;
  /** Nome da conta titular (dado do operador, não do cliente final). */
  subjectName?: string | null;
}

/** Corpo de `POST /platform/agent-clients/operators` (`RegisterOperatorClientRequest`). */
export interface EnableOperatorClientPayload {
  subjectAccountId: string;
  maxScopes: AgentScope[];
  reason: AgentManagementReason;
  detail?: string;
}

/**
 * Habilita o operador no Claude Code: cria o cliente `Operator` da conta (`op-<tag>-<conta>`),
 * com o teto dentro do papel dela. O redirect de loopback é do servidor. Exige login recente.
 */
export async function enableOperatorClient(payload: EnableOperatorClientPayload): Promise<AgentClient> {
  const { data } = await api.post<AgentClient>(`${AGENT_CLIENTS}/operators`, withOptionalDetail(payload));
  return data;
}

/** O cliente `Operator` do próprio titular neste ambiente (`OperatorSelfClientDto`). */
export interface OperatorSelfClient {
  id: string;
  oauthClientId: string;
  status: AgentClientStatus;
  maxScopes: AgentScope[];
  certificationDueAt: string;
  /**
   * O segredo do conector web do claude.ai (OA-D12): se existe e quando foi gerado, nunca o valor
   * nem o hash. Ausente na API anterior.
   */
  webConnectorSecret?: OperatorWebConnectorSecretState | null;
}

/** Estado do segredo do conector web (`OperatorWebConnectorSecretStateDto`). */
export interface OperatorWebConnectorSecretState {
  present: boolean;
  issuedAt: string | null;
}

/**
 * A resposta de quem gera ou gira o segredo do conector web (`OperatorWebConnectorSecretIssuedDto`):
 * a única vez em que o segredo aparece (`Cache-Control: no-store`). Fica só em memória do componente
 * que o mostra: nunca em storage, log ou analytics.
 */
export interface OperatorWebConnectorSecretIssued {
  secret: string;
  issuedAt: string;
  oauthClientId: string;
  /** A concessão do claude.ai revogada ao girar, se havia. */
  revokedGrantId: string | null;
}

/** Uma concessão pessoal do próprio titular (`OperatorSelfGrantDto`), sem token, hash nem code. */
export interface OperatorSelfGrant {
  id: string;
  status: AgentGrantStatus;
  scopes: AgentScope[];
  approvedAt: string;
  expiresAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

/**
 * `GET /platform/me/operator-access` (tarefa 4.3): o caminho do operador neste ambiente e o acesso
 * pessoal do próprio titular, de qualquer papel. `enabled` e `allowedScopes` também decidem, em
 * `/agentes`, se a habilitação aparece e que teto ela oferece.
 */
export interface OperatorSelfAccess {
  enabled: boolean;
  /** O teto do ambiente (`AllowedScopes`), só escopos de leitura; em produção, só métricas. */
  allowedScopes: AgentScope[];
  client: OperatorSelfClient | null;
  /** As concessões pessoais, das mais novas para as mais antigas (no máximo 20). */
  grants: OperatorSelfGrant[];
}

const MY_OPERATOR_ACCESS = '/platform/me/operator-access';
const MY_OPERATOR_GRANTS = '/platform/me/operator-grants';

export async function getMyOperatorAccess(): Promise<OperatorSelfAccess> {
  const { data } = await api.get<OperatorSelfAccess>(MY_OPERATOR_ACCESS);
  return data;
}

/**
 * Revoga a própria concessão pessoal: a próxima chamada do Claude Code recebe 401. Idempotente
 * (revogar de novo dá 200); concessão de outra conta é 404, como a inexistente.
 */
export async function revokeMyOperatorGrant(id: string): Promise<OperatorSelfGrant> {
  const { data } = await api.post<OperatorSelfGrant>(`${MY_OPERATOR_GRANTS}/${encodeURIComponent(id)}/revoke`, {});
  return data;
}

const MY_WEB_CONNECTOR_SECRET = '/platform/me/operator-client/web-connector-secret';

/**
 * Gera ou gira o segredo do conector web do claude.ai do próprio titular (OA-D12), com o step-up de
 * propósito `operator_web_connector_secret.issue` (alvo: a própria conta). Sem corpo e sem
 * `Idempotency-Key`: a resposta carrega o segredo, que a API não guarda em claro. Girar invalida o
 * anterior e revoga a concessão nascida pelo claude.ai.
 */
export async function issueMyWebConnectorSecret(stepUp: StepUpProof): Promise<OperatorWebConnectorSecretIssued> {
  const { data } = await api.post<OperatorWebConnectorSecretIssued>(MY_WEB_CONNECTOR_SECRET, undefined, {
    headers: stepUpHeaders(stepUp),
  });
  return data;
}

/** Apaga o segredo do conector web (sem step-up, idempotente: `204` também sem segredo). */
export async function deleteMyWebConnectorSecret(): Promise<void> {
  await api.delete(MY_WEB_CONNECTOR_SECRET);
}

// =============================================================================
//  Preparar autorização (change platform-mcp-writes, tarefa 1.18)
// =============================================================================
//
// Tipos de `PlatformAgentAuthorizationIntentRequests.cs`. Só Admin, com `PlatformJwt`. A preparação
// leva o step-up preso ao cliente (`agent_authorization_intent.prepare`): passkey com escrita direta
// ou controle total, TOTP (ou passkey) só com proposta. Sem `Idempotency-Key`: preparar de novo
// substitui a anterior.

/** `Prepared`, `Expired` (preparada e vencida), `Consumed` ou `Cancelled`. */
export type AgentAuthorizationIntentStatus = 'Prepared' | 'Expired' | 'Consumed' | 'Cancelled';

export interface AgentAuthorizationIntent {
  id: string;
  agentClientId: string;
  profile: string;
  scopes: AgentWriteScopeItem[];
  status: AgentAuthorizationIntentStatus;
  stepUpMethod: 'passkey' | 'totp';
  createdAt: string;
  expiresAt: string;
  consumedAt: string | null;
  cancelledAt: string | null;
  preparedByAccountId: string;
  preparedByName: string;
  /** Preparada por esta conta nesta sessão: só ela vale no consentimento. */
  mine: boolean;
}

export interface AgentAuthorizationIntentList {
  agentClientId: string;
  profile: string;
  writeEligible: boolean;
  fullControlEligible: boolean;
  intentMinutes: number;
  fullControlWindowMinutes: number;
  availableScopes: AgentWriteScopeItem[];
  intents: AgentAuthorizationIntent[];
}

const AGENT_AUTHORIZATION_INTENTS = '/platform/agent-authorization-intents';

export async function listAgentAuthorizationIntents(agentClientId: string): Promise<AgentAuthorizationIntentList> {
  const { data } = await api.get<AgentAuthorizationIntentList>(AGENT_AUTHORIZATION_INTENTS, {
    params: { agentClientId },
  });
  return data;
}

/** Prepara a escrita do perfil com o step-up (passkey com escrita direta ou controle total). */
export async function prepareAgentAuthorizationIntent(
  payload: { agentClientId: string; scopes: AgentWriteScope[] },
  stepUp: StepUpProof,
): Promise<AgentAuthorizationIntent> {
  const { data } = await api.post<AgentAuthorizationIntent>(AGENT_AUTHORIZATION_INTENTS, payload, {
    headers: stepUpHeaders(stepUp),
  });
  return data;
}

export async function cancelAgentAuthorizationIntent(id: string): Promise<void> {
  await api.post(`${AGENT_AUTHORIZATION_INTENTS}/${encodeURIComponent(id)}/cancel`, {});
}

/**
 * Erros por campo de um 400 de validação (`ValidationProblem`), como a API manda: a chave é o
 * campo do corpo (`units`, `price`, `rates[0].category`). Vazio quando o erro não traz campos.
 */
export function problemFieldErrors(err: unknown): Record<string, string[]> {
  if (!(err instanceof AxiosError) || err.response?.status !== 400) return {};
  const errors = (err.response.data as { errors?: unknown } | undefined)?.errors;
  if (!errors || typeof errors !== 'object') return {};
  const result: Record<string, string[]> = {};
  for (const [field, messages] of Object.entries(errors as Record<string, unknown>)) {
    if (Array.isArray(messages)) {
      const texts = messages.filter((m): m is string => typeof m === 'string');
      if (texts.length > 0) result[field] = texts;
    }
  }
  return result;
}

/** 404 sem `errorCode`: a rota não existe nesta versão da API (imagem anterior). */
export function isRouteMissing(err: unknown): boolean {
  return problemStatus(err) === 404 && problemErrorCode(err) === null;
}

/** Mensagem da rota que a API desta versão ainda não tem. */
export const ROUTE_MISSING_MESSAGE = 'Recurso indisponível nesta versão da API.';

// =============================================================================
//  Atendimentos do Atendente IA no backoffice (change billing-ai-units, decisões 25 e 27)
// =============================================================================
//
// Os literais são os `[JsonStringEnumMemberName]` da API, comparados por igualdade. Nenhuma
// resposta daqui traz conteúdo nem identificador de conversa: o extrato diz "23 atendimentos",
// nunca de quem. Toda mutação leva `Idempotency-Key` (`IdempotencyKeyFilter`); a tela que repete o
// mesmo pedido depois de um timeout ou de um 5xx passa a chave dele (`useIdempotencyKey`).

/** De onde veio um lote de atendimentos. */
export type AiUnitLotSource = 'plan_cycle' | 'plan_change_proration' | 'purchase' | 'concession';
export type AiUnitEntryKind = 'grant' | 'consume' | 'expire' | 'revoke' | 'restore';
export type AiUnitBalanceState = 'ok' | 'low' | 'exhausted';
export type AiUnitPurchaseStatus = 'pending' | 'paid' | 'expired' | 'refunded' | 'charged_back';
/** Formas de pagamento de um pacote: sem boleto. */
export type AiUnitPaymentMethod = 'pix' | 'credit_card';
/** Onde está o reembolso pedido pela plataforma (`AiUnitRefundState`). */
export type AiUnitRefundState = 'none' | 'pending_at_gateway' | 'gave_up' | 'done';
/** Por que a compra não cabe no reembolso de 7 dias (`AiUnitRefundRefusals`). */
export type AiUnitRefundRefusal = 'not_paid' | 'window_expired' | 'units_used';

// --- Pacotes (`/platform/ai-unit-packs`, só Admin) ---------------------------

/** Uma versão de um pacote; compra já feita guarda o retrato da versão vendida. */
export interface AiUnitPackVersion {
  id: string;
  code: string;
  units: number;
  price: number;
  /** Preço por atendimento, com quatro casas. */
  unitPrice: number;
  validityMonths: number;
  effectiveFrom: string;
  archivedAt: string | null;
  /** Arquivada antes de valer: o arquivamento a cancelou e ela nunca passa a valer. */
  cancelledByArchive: boolean;
  createdAt: string;
  createdByOperatorId: string | null;
}

/** Um código do catálogo: o que vale, o que está agendado, se está à venda e o histórico. */
export interface AiUnitPack {
  code: string;
  isForSale: boolean;
  isArchived: boolean;
  current: AiUnitPackVersion | null;
  /** A próxima versão agendada; as que o arquivamento cancelou não contam. */
  scheduled: AiUnitPackVersion | null;
  /** Todas as versões do código. */
  versions: AiUnitPackVersion[];
}

export interface SaveAiUnitPackPayload {
  code: string;
  units: number;
  price: number;
  validityMonths: number;
  /** ISO 8601; ausente vale agora, no futuro fica agendada. */
  effectiveFrom?: string | null;
}

export interface SaveAiUnitPackResponse {
  version: AiUnitPackVersion;
  /** O código estava arquivado e volta à venda quando esta versão valer. */
  reactivated: boolean;
  pack: AiUnitPack;
}

export const AI_UNIT_PACKS_URL = '/platform/ai-unit-packs';

/** O catálogo de pacotes por código, com as versões. */
export async function listAiUnitPacks(): Promise<AiUnitPack[]> {
  const { data } = await api.get<{ packs: AiUnitPack[] }>(AI_UNIT_PACKS_URL);
  return data.packs;
}

/**
 * Cria uma versão de um pacote (código novo ou existente). 400 com os erros por campo (`code`,
 * `units`, `price`, `validityMonths`, `effectiveFrom`), para a tela mostrá-los junto do campo.
 */
export async function saveAiUnitPack(
  payload: SaveAiUnitPackPayload,
  options: SensitiveWriteOptions = {},
): Promise<SaveAiUnitPackResponse> {
  const { data } = await api.post<SaveAiUnitPackResponse>(AI_UNIT_PACKS_URL, payload, {
    headers: sensitiveWriteHeaders({ idempotencyKey: options.idempotencyKey }),
  });
  return data;
}

export function archiveAiUnitPackUrl(code: string): string {
  return `${AI_UNIT_PACKS_URL}/${encodeURIComponent(code)}/archive`;
}

/**
 * Tira o código da venda agora e cancela a versão agendada dele. O código já arquivado responde
 * 200 sem mudar nada; o inexistente, 404 `billing.ai_units_pack_not_found`.
 */
export async function archiveAiUnitPack(code: string, options: SensitiveWriteOptions = {}): Promise<AiUnitPack> {
  const { data } = await api.post<AiUnitPack>(archiveAiUnitPackUrl(code), {}, {
    headers: sensitiveWriteHeaders({ idempotencyKey: options.idempotencyKey }),
  });
  return data;
}

// --- Atendimentos do tenant (`/platform/tenants/{tenantId}/ai-units`) --------

/** A cota do ciclo vigente. */
export interface AiUnitsCycle {
  used: number;
  total: number;
  startsAt: string;
  /** Fim exclusivo do ciclo: a renovação. */
  endsAt: string;
}

/** O vencimento mais próximo entre os lotes com sobra. */
export interface AiUnitExpiry {
  at: string;
  units: number;
}

/** Um lote da carteira, vigente ou fechado, com a origem e o operador da cortesia. */
export interface PlatformAiUnitLot {
  lotId: string;
  source: AiUnitLotSource;
  units: number;
  remainingUnits: number;
  grantedAt: string;
  expiresAt: string;
  closedAt: string | null;
  /** Aberto e dentro da validade agora. */
  isActive: boolean;
  /** Só no lote de pacote. */
  purchaseId: string | null;
  /** Só na cortesia. */
  grantedByOperatorId: string | null;
  /** O motivo da cortesia: nota interna, nunca mostrada ao dono. */
  reason: string | null;
}

/** Um lançamento, linha a linha, sem a chave da operação (que no débito leva o id do atendimento). */
export interface PlatformAiUnitEntry {
  entryId: string;
  occurredAt: string;
  kind: AiUnitEntryKind;
  /** Com sinal: positiva na concessão e na devolução. */
  units: number;
  /** A descrição em pt-BR, a mesma do extrato do dono. */
  description: string;
  lotId: string | null;
  source: AiUnitLotSource | null;
  /** Consumo de carência, sem lote. */
  isGrace: boolean;
  /** Abatimento de uma dívida de carência num lote. */
  isSettlement: boolean;
  actorOperatorId: string | null;
  reason: string | null;
}

/** O reembolso de uma compra pela plataforma: onde está e se ainda cabe. */
export interface PlatformAiUnitRefund {
  state: AiUnitRefundState;
  /** Paga, dentro dos 7 dias, sem reembolso pedido e com o lote intacto. */
  eligible: boolean;
  /** Nulo quando cabe ou quando o reembolso já foi pedido. */
  refusalReason: AiUnitRefundRefusal | null;
  windowEndsAt: string | null;
  requestedAt: string | null;
  requestedByOperatorId: string | null;
  attempts: number;
  gatewayStatus: string | null;
  refundedAt: string | null;
}

/** Uma compra de pacote, sem o copia-e-cola do Pix nem a chave de idempotência do painel. */
export interface PlatformAiUnitPurchase {
  purchaseId: string;
  packCode: string;
  units: number;
  amount: number;
  validityMonths: number;
  paymentMethod: AiUnitPaymentMethod;
  status: AiUnitPurchaseStatus;
  asaasPaymentId: string | null;
  createdAt: string;
  paidAt: string | null;
  expiredAt: string | null;
  chargedBackAt: string | null;
  lotId: string | null;
  refund: PlatformAiUnitRefund;
}

/** `GET /platform/tenants/{tenantId}/ai-units`. */
export interface PlatformAiUnitsResponse {
  tenantId: string;
  /** O direito vigente inclui o Atendente IA; sem ele, os atendimentos ficam parados e invisíveis ao dono. */
  hasAttendantPlan: boolean;
  state: AiUnitBalanceState;
  cycle: AiUnitsCycle | null;
  packUnits: number;
  graceDebt: number;
  nextExpiry: AiUnitExpiry | null;
  /** Os vigentes primeiro, na ordem de consumo; depois até 100 fechados. */
  lots: PlatformAiUnitLot[];
  /** Os últimos 100 lançamentos. */
  entries: PlatformAiUnitEntry[];
  /** Até 100 compras, da mais recente para a mais antiga. */
  purchases: PlatformAiUnitPurchase[];
}

/** O lote de cortesia como a concessão e o estorno o devolvem. */
export interface AiUnitConcessionLot {
  lotId: string;
  tenantId: string;
  source: AiUnitLotSource;
  units: number;
  /** O restante, já descontada a carência pendente na concessão. */
  remainingUnits: number;
  grantedAt: string;
  expiresAt: string;
  closedAt: string | null;
  grantedByOperatorId: string | null;
  reason: string | null;
}

export interface GrantAiUnitsPayload {
  /** De 1 a 10.000. */
  units: number;
  /** Do dia seguinte (São Paulo) a 365 dias. */
  expiresAt: string;
  /** De 3 a 500 caracteres; nota interna. */
  reason: string;
}

export interface RevokeAiUnitsGrantResponse {
  lot: AiUnitConcessionLot;
  /** Falso quando o lote já estava fechado ou vencido: nada mudou. */
  revoked: boolean;
  revokedUnits: number;
  usedUnits: number;
}

export interface RefundAiUnitPurchaseResult {
  purchase: PlatformAiUnitPurchase;
  /** 202: o pacote já foi estornado, e o Asaas ainda não confirmou o reembolso. */
  pendingAtGateway: boolean;
}

function tenantAiUnitsUrl(tenantId: string): string {
  return `/platform/tenants/${encodeURIComponent(tenantId)}/ai-units`;
}

export function grantTenantAiUnitsUrl(tenantId: string): string {
  return `${tenantAiUnitsUrl(tenantId)}/grants`;
}

export function revokeTenantAiUnitsGrantUrl(tenantId: string, lotId: string): string {
  return `${tenantAiUnitsUrl(tenantId)}/grants/${encodeURIComponent(lotId)}/revoke`;
}

export function refundTenantAiUnitPurchaseUrl(tenantId: string, purchaseId: string): string {
  return `${tenantAiUnitsUrl(tenantId)}/purchases/${encodeURIComponent(purchaseId)}/refund`;
}

/** A carteira do tenant (Commercial e Admin). Tenant inativo responde 422. */
export async function getTenantAiUnits(tenantId: string): Promise<PlatformAiUnitsResponse> {
  const { data } = await api.get<PlatformAiUnitsResponse>(tenantAiUnitsUrl(tenantId));
  return data;
}

/** Concede uma cortesia (Commercial e Admin): 201 com o lote, já descontada a carência. */
export async function grantTenantAiUnits(
  tenantId: string,
  payload: GrantAiUnitsPayload,
  options: SensitiveWriteOptions = {},
): Promise<AiUnitConcessionLot> {
  const { data } = await api.post<AiUnitConcessionLot>(grantTenantAiUnitsUrl(tenantId), payload, {
    headers: sensitiveWriteHeaders({ idempotencyKey: options.idempotencyKey }),
  });
  return data;
}

/**
 * Estorna o que sobrou de uma cortesia (só Admin). Lote de outra origem, de outro tenant ou
 * inexistente responde 404; o já fechado responde 200 com `revoked = false`.
 */
export async function revokeTenantAiUnitsGrant(
  tenantId: string,
  lotId: string,
  reason: string,
  options: SensitiveWriteOptions = {},
): Promise<RevokeAiUnitsGrantResponse> {
  const { data } = await api.post<RevokeAiUnitsGrantResponse>(
    revokeTenantAiUnitsGrantUrl(tenantId, lotId),
    { reason },
    { headers: sensitiveWriteHeaders({ idempotencyKey: options.idempotencyKey }) },
  );
  return data;
}

/**
 * Reembolso integral de um pacote (só Admin). 200 com o estorno confirmado pelo Asaas, 202 com o
 * pacote estornado e o reembolso pendente no gateway; 409 `billing.ai_units_refund_not_allowed`
 * com a extensão `reason`; 404 `billing.ai_units_purchase_not_found`. Devolve dinheiro e não se
 * desfaz: exige o step-up (`stepUpCode`, 403 `platform.step_up_required` sem ele), conferido
 * antes da `Idempotency-Key` da tentativa.
 */
export async function refundTenantAiUnitPurchase(
  tenantId: string,
  purchaseId: string,
  reason: string,
  options: SensitiveWriteOptions = {},
): Promise<RefundAiUnitPurchaseResult> {
  const response = await api.post<PlatformAiUnitPurchase>(
    refundTenantAiUnitPurchaseUrl(tenantId, purchaseId),
    { reason },
    { headers: sensitiveWriteHeaders(options) },
  );
  return { purchase: response.data, pendingAtGateway: response.status === 202 };
}

/** O motivo do 409 `billing.ai_units_refund_not_allowed`, quando a API manda um dos três. */
export function refundRefusalOf(err: unknown): AiUnitRefundRefusal | null {
  if (problemErrorCode(err) !== 'billing.ai_units_refund_not_allowed' || !(err instanceof AxiosError)) return null;
  const reason = (err.response?.data as { reason?: unknown } | undefined)?.reason;
  return reason === 'not_paid' || reason === 'window_expired' || reason === 'units_used' ? reason : null;
}

// --- Custos do Atendente IA (`/platform/customer-agent`, só Admin) -----------

/** Uma linha da tabela da Meta. */
export interface MetaMessageRate {
  id: string;
  /** Como a Meta manda em `pricing.category` (`service`, `utility`). */
  category: string;
  usdPerMessage: number;
  /** Primeiro dia da vigência em São Paulo (AAAA-MM-DD). */
  effectiveFrom: string;
  referenceBrlPerUsd: number | null;
  createdAt: string;
  createdByOperatorId: string;
}

/** Uma categoria: a tarifa vigente hoje, as agendadas e todas as versões. */
export interface MetaMessageRateCategory {
  category: string;
  current: MetaMessageRate | null;
  /** Da mais próxima para a mais distante. */
  scheduled: MetaMessageRate[];
  /** Da mais nova para a mais antiga. */
  versions: MetaMessageRate[];
}

export interface MetaMessageRatesResponse {
  /** Hoje em São Paulo (AAAA-MM-DD), que separa a vigente das agendadas. */
  today: string;
  categories: MetaMessageRateCategory[];
}

export interface SaveMetaMessageRatesPayload {
  /** AAAA-MM-DD, de hoje em diante; ausente vale hoje. */
  effectiveFrom?: string | null;
  referenceBrlPerUsd?: number | null;
  /** De 1 a 20 categorias, sem repetir. */
  rates: Array<{ category: string; usdPerMessage: number }>;
}

export interface SaveMetaMessageRatesResponse {
  saved: MetaMessageRate[];
  table: MetaMessageRatesResponse;
}

export const META_RATES_URL = '/platform/customer-agent/meta-rates';

/** A tabela da Meta, global, sem dado de tenant. */
export async function listMetaMessageRates(): Promise<MetaMessageRatesResponse> {
  const { data } = await api.get<MetaMessageRatesResponse>(META_RATES_URL);
  return data;
}

/**
 * Cadastra uma vigência para uma ou mais categorias. 400 por campo (`effectiveFrom`,
 * `referenceBrlPerUsd`, `rates`, `rates[i].category`, `rates[i].usdPerMessage`), sem gravar nada.
 */
export async function saveMetaMessageRates(
  payload: SaveMetaMessageRatesPayload,
  options: SensitiveWriteOptions = {},
): Promise<SaveMetaMessageRatesResponse> {
  const { data } = await api.post<SaveMetaMessageRatesResponse>(META_RATES_URL, payload, {
    headers: sensitiveWriteHeaders({ idempotencyKey: options.idempotencyKey }),
  });
  return data;
}

/**
 * Um teto mensal em dólar: o efetivo, a sobreposição gravada pelo backoffice e o padrão de
 * configuração (`CustomerAgent:Budget:*`). Efetivo = sobreposição, quando há, senão o padrão.
 */
export interface CustomerAgentBudgetCap {
  effectiveUsd: number;
  overrideUsd: number | null;
  defaultUsd: number;
}

/**
 * `GET /platform/customer-agent/budget` (design da change billing-ai-units, decisões 23 e 27): os
 * dois tetos e o gasto estimado do mês na plataforma, um número só, sem tenant.
 */
export interface CustomerAgentBudgetResponse {
  /** `AAAA-MM`, no calendário de São Paulo. */
  month: string;
  global: CustomerAgentBudgetCap;
  /** Um valor único, aplicado a cada loja. */
  perTenant: CustomerAgentBudgetCap;
  estimatedSpentUsd: number;
  updatedAt: string | null;
  updatedByOperatorId: string | null;
}

/** Nulo apaga a sobreposição e volta ao padrão de configuração. */
export interface SaveCustomerAgentBudgetPayload {
  globalMonthlyUsd: number | null;
  tenantMonthlyUsd: number | null;
}

export const CUSTOMER_AGENT_BUDGET_URL = '/platform/customer-agent/budget';

/**
 * Os tetos em dólar do Atendente IA. Nulo quando a API desta versão ainda não tem a rota (404 sem
 * `errorCode`): ela entra com o módulo do atendente.
 */
export async function getCustomerAgentBudget(): Promise<CustomerAgentBudgetResponse | null> {
  try {
    const { data } = await api.get<CustomerAgentBudgetResponse>(CUSTOMER_AGENT_BUDGET_URL);
    return data;
  } catch (err) {
    if (isRouteMissing(err)) return null;
    throw err;
  }
}

/** Grava as sobreposições (1 a 100.000, ou nulo), com o teto por loja nunca acima do global. */
export async function saveCustomerAgentBudget(
  payload: SaveCustomerAgentBudgetPayload,
  options: SensitiveWriteOptions = {},
): Promise<CustomerAgentBudgetResponse> {
  const { data } = await api.put<CustomerAgentBudgetResponse>(CUSTOMER_AGENT_BUDGET_URL, payload, {
    headers: sensitiveWriteHeaders({ idempotencyKey: options.idempotencyKey }),
  });
  return data;
}
